<template>
  <v-container fluid class="pa-4">
    <!-- Header -->
    <v-row>
      <v-col cols="12">
        <div class="d-flex align-center">
          <v-btn
            v-if="selectedRequestId && activeTab === 0"
            icon
            class="mr-2"
            @click="goBack"
          >
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <h1 class="headline">
            Employee App
            <span v-if="selectedRequestId && activeTab === 0 && selectedRequest" class="grey--text font-weight-regular">
              / {{ formatWeekRange(selectedRequest.weekStart, selectedRequest.weekEnd) }}
            </span>
          </h1>
        </div>
      </v-col>
    </v-row>

    <!-- Tabs -->
    <v-row class="mt-2" v-if="!selectedRequestId">
      <v-col cols="12">
        <v-tabs ref="tabs" v-model="activeTab" background-color="transparent">
          <v-tab>
            <v-icon left small>mdi-clipboard-text</v-icon>
            Timesheets
          </v-tab>
          <v-tab>
            <v-icon left small>mdi-alert-circle</v-icon>
            Disputes
            <v-chip v-if="pendingDisputesCount > 0" x-small color="warning" class="ml-2">
              {{ pendingDisputesCount }}
            </v-chip>
          </v-tab>
          <v-tab>
            <v-icon left small>mdi-message-text-outline</v-icon>
            Messages
          </v-tab>
          <v-tab>
            <v-icon left small>mdi-file-document-multiple</v-icon>
            Uploads
          </v-tab>
          <v-tab>
            <v-icon left small>mdi-emoticon-sick-outline</v-icon>
            Sick Days
            <v-chip v-if="pendingSickDaysCount > 0" x-small color="warning" class="ml-2">
              {{ pendingSickDaysCount }}
            </v-chip>
          </v-tab>
        </v-tabs>
      </v-col>
    </v-row>

    <!-- Loading -->
    <v-row v-if="loading" justify="center" class="mt-8">
      <v-col cols="12" class="text-center">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        <div class="mt-4 grey--text">Loading timesheets...</div>
      </v-col>
    </v-row>

    <!-- Tab Content -->
    <template v-if="!loading">
      <!-- Timesheets Tab -->
      <template v-if="activeTab === 0">
        <!-- Requests List View (when no request selected) -->
        <v-row v-if="!selectedRequestId" class="mt-4">
          <v-col cols="12">
            <!-- Create New Request Card -->
            <v-card outlined class="mb-4">
              <v-card-title>
                <v-icon left>mdi-plus</v-icon>
                Create Timesheet Request
              </v-card-title>
              <v-card-text>
                <v-row align="center">
                  <v-col cols="12" sm="5">
                    <v-menu
                      v-model="requestStartDateMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="newRequestStartDate"
                          label="Start Date"
                          prepend-icon="mdi-calendar"
                          readonly
                          outlined
                          dense
                          hide-details
                          v-bind="attrs"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="newRequestStartDate"
                        @input="requestStartDateMenu = false"
                        :allowed-dates="allowedStartDates"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col cols="12" sm="5">
                    <v-menu
                      v-model="requestEndDateMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="newRequestEndDate"
                          label="End Date"
                          prepend-icon="mdi-calendar"
                          readonly
                          outlined
                          dense
                          hide-details
                          v-bind="attrs"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="newRequestEndDate"
                        @input="requestEndDateMenu = false"
                        :min="newRequestStartDate"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col cols="12" sm="2">
                    <v-btn
                      color="primary"
                      @click="createTimesheetRequest"
                      :loading="creatingRequest"
                      :disabled="!canCreateRequest"
                      block
                    >
                      <v-icon left>mdi-send</v-icon>
                      Request
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- Existing Requests Table -->
            <v-card outlined>
              <v-card-title>
                <v-icon left>mdi-history</v-icon>
                Timesheet Requests
                <v-spacer></v-spacer>
                <v-btn icon small @click="loadTimesheetRequests" :loading="loadingRequests">
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
              </v-card-title>
              <v-data-table
                :headers="requestsHeaders"
                :items="timesheetRequests"
                :items-per-page="15"
                class="elevation-0"
                :loading="loadingRequests"
              >
                <template v-slot:item.weekRange="{ item }">
                  {{ formatWeekRange(item.weekStart, item.weekEnd) }}
                </template>
                <template v-slot:item.createdAt="{ item }">
                  {{ formatShortDateTime(item.createdAt) }}
                </template>
                <template v-slot:item.submissionStatus="{ item }">
                  <v-chip small :color="getRequestStatusColor(item)" dark>
                    <template v-if="typeof item.expectedCount === 'number'">
                      {{ item.submittedCount || 0 }} of {{ item.expectedCount }} submitted
                    </template>
                    <template v-else>
                      {{ item.submittedCount || 0 }} submitted
                    </template>
                  </v-chip>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        x-small
                        class="ml-1"
                        v-on="on"
                        v-bind="attrs"
                        :loading="refreshingCountId === item.id"
                        @click="refreshExpectedCount(item)"
                      >
                        <v-icon x-small>mdi-refresh</v-icon>
                      </v-btn>
                    </template>
                    <span>Recount employees who worked this week</span>
                  </v-tooltip>
                </template>
                <template v-slot:item.status="{ item }">
                  <v-chip small :color="item.status === 'active' ? 'success' : 'grey'" dark class="text-capitalize">
                    {{ item.status || 'active' }}
                  </v-chip>
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-btn
                    v-if="item.status === 'active'"
                    class="mr-2"
                    small
                    text
                    outlined
                    color="grey"
                    @click="confirmCloseRequest(item)"
                    :loading="closingRequestId === item.id"
                  >
                    <v-icon small left>mdi-close</v-icon>
                    Close
                  </v-btn>
                  <v-btn
                    v-if="item.status === 'closed'"
                    class="mr-2"
                    small
                    text
                    outlined
                    color="success"
                    @click="reopenTimesheetRequest(item)"
                    :loading="reopeningRequestId === item.id"
                  >
                    <v-icon small left>mdi-refresh</v-icon>
                    Reopen
                  </v-btn>
                  <v-btn
                    small
                    text
                    outlined
                    color="primary"
                    @click="viewTimesheetRequest(item)"
                  >
                    <v-icon small left>mdi-eye</v-icon>
                    View
                  </v-btn>
                </template>
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>

        <!-- Timesheets Detail View (when request is selected) -->
        <v-row v-else class="mt-4">
          <v-col cols="12">
            <!-- Who still owes a signature for this week. Loaded on demand - it reads
                 every punch in the week to find who worked it. -->
            <v-card v-if="!loadingRequestTimesheets && selectedRequest" outlined class="mb-4">
              <v-card-title class="py-2">
                <span class="body-1">Not signed</span>
                <v-spacer></v-spacer>
                <v-btn small text color="primary" :loading="loadingUnsigned" @click="loadUnsignedEmployees">
                  <v-icon left small>mdi-account-alert</v-icon>
                  {{ unsignedEmployees ? 'Refresh' : "Who hasn't signed" }}
                </v-btn>
              </v-card-title>
              <v-card-text v-if="unsignedEmployees">
                <div class="caption grey--text mb-1">
                  Not signed yet ({{ unsignedEmployees.length }}) - worked this week, no signed timesheet
                </div>
                <div v-if="!unsignedEmployees.length" class="body-2 mb-2">Everyone who worked this week has signed.</div>
                <v-chip
                  v-for="employee in unsignedEmployees"
                  :key="employee.id"
                  small
                  class="mr-1 mb-1"
                >
                  {{ employee.name }} <span class="grey--text ml-1">#{{ employee.badgeNumber }}</span>
                </v-chip>
                <div class="caption grey--text mt-3 mb-1">
                  Needs re-sign ({{ resignEmployees.length }}) - sent back after a change, not signed again yet
                </div>
                <div v-if="!resignEmployees.length" class="body-2">None waiting.</div>
                <v-chip
                  v-for="employee in resignEmployees"
                  :key="employee.id"
                  small
                  color="orange lighten-4"
                  class="mr-1 mb-1"
                >
                  {{ employee.name }} <span class="grey--text ml-1">#{{ employee.badgeNumber }}</span>
                </v-chip>
              </v-card-text>
            </v-card>

            <!-- Loading state -->
            <v-card v-if="loadingRequestTimesheets" outlined class="pa-8 text-center">
              <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
              <div class="mt-4 grey--text">Loading timesheets...</div>
            </v-card>

            <!-- Empty state for this request -->
            <v-card v-else-if="requestTimesheets.length === 0" outlined class="pa-8 text-center">
              <v-icon size="64" color="grey lighten-1">mdi-clipboard-text-off</v-icon>
              <div class="mt-4 grey--text text-h6">No timesheets submitted yet</div>
              <div class="caption grey--text">Employees will submit timesheets through the employee app</div>
              <v-btn color="primary" text class="mt-4" @click="goBack">
                <v-icon left>mdi-arrow-left</v-icon>
                Back to all requests
              </v-btn>
            </v-card>

            <v-card v-else outlined>
              <v-card-title class="py-2">
                <span class="body-1">
                  {{ requestTimesheets.length }} timesheet{{ requestTimesheets.length !== 1 ? 's' : '' }} submitted
                </span>
                <v-spacer></v-spacer>
                <v-btn
                  v-if="requestHasPdfs"
                  small
                  color="primary"
                  outlined
                  @click="downloadAllPdfs"
                  :loading="downloadingPdfs"
                  :disabled="downloadingPdfs"
                >
                  <v-icon left small>mdi-download</v-icon>
                  Download All PDFs
                </v-btn>
              </v-card-title>
              <v-data-table
                :headers="timesheetsHeaders"
                :items="requestTimesheets"
                :items-per-page="25"
                class="elevation-0"
              >
                <template v-slot:item.status="{ item }">
                  <v-chip small :color="getStatusChipColor(item.status)" dark>
                    {{ getStatusText(item.status) }}
                  </v-chip>
                  <v-chip
                    small
                    :color="getTimesheetDisputeChipColor(item)"
                    class="ml-1"
                    v-if="item.hasDisputes"
                  >
                    {{ getTimesheetDisputeChipText(item) }}
                  </v-chip>
                </template>
                <template v-slot:item.weeklyTotal="{ item }">
                  {{ formatHours(item.weeklyTotal) }}
                </template>
                <template v-slot:item.submittedAt="{ item }">
                  {{ formatShortDateTime(item.submittedAt) }}
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-btn icon small @click.stop="openPdf(item)" v-if="item.pdfUrl" title="View PDF">
                    <v-icon small>mdi-file-pdf-box</v-icon>
                  </v-btn>
                  <v-btn small text color="primary" @click.stop="openTimesheetDialog(item)">
                    View
                  </v-btn>
                </template>
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>
      </template>

      <!-- Disputes Tab (Clock-out Disputes) -->
      <template v-if="activeTab === 1">
        <v-row>
          <v-col cols="12">
            <!-- Loading. Without this the empty state below renders while the fetch
                 is still running, telling payroll every dispute is resolved. -->
            <v-card v-if="loadingDisputes" outlined class="pa-6">
              <v-skeleton-loader type="table-heading, table-row@4"></v-skeleton-loader>
            </v-card>

            <!-- No disputes state -->
            <v-card v-else-if="punchDisputes.length === 0" outlined class="pa-8 text-center">
              <v-icon size="64" color="grey lighten-1">mdi-check-circle</v-icon>
              <div class="mt-4 grey--text text-h6">No disputes found</div>
              <div class="caption grey--text">All clock-out disputes have been resolved</div>
            </v-card>

            <!-- Disputes Table -->
            <v-card v-else outlined>
              <v-card-title class="py-2">
                <span class="body-1 grey--text">
                  {{ pendingDisputesCount }} pending dispute{{ pendingDisputesCount !== 1 ? 's' : '' }}
                </span>
                <v-spacer></v-spacer>
                <v-text-field
                  v-model="disputeSearch"
                  label="Filter by employee, badge, department or job site"
                  prepend-inner-icon="mdi-magnify"
                  clearable
                  dense
                  outlined
                  hide-details
                  class="mr-3"
                  style="max-width: 360px"
                ></v-text-field>
                <v-btn-toggle v-model="disputeFilter" dense mandatory>
                  <v-btn small value="pending">
                    Pending
                  </v-btn>
                  <v-btn small value="resolved">
                    Resolved
                  </v-btn>
                  <v-btn small value="all">
                    All
                  </v-btn>
                </v-btn-toggle>
              </v-card-title>
              <v-data-table
                :headers="disputesHeaders"
                :items="filteredDisputes"
                :items-per-page="15"
                class="elevation-0"
              >
                <template v-slot:item.department="{ item }">
                  <span v-if="item.department">{{ item.department }}</span>
                  <span v-else class="grey--text">-</span>
                </template>
                <template v-slot:item.date="{ item }">
                  {{ formatDate(item.date) }}
                </template>
                <template v-slot:item.punchTimes="{ item }">
                  <!-- A dispute belongs to one punch, so only that punch is shown.
                       The in/out pair below is kept for records written before that
                       was true. -->
                  <div v-if="!item.isLegacyPair" class="d-flex align-center">
                    <v-icon x-small :color="item.disputeDirection === 'in' ? 'success' : 'error'" class="mr-1">
                      {{ item.disputeDirection === 'in' ? 'mdi-login' : 'mdi-logout' }}
                    </v-icon>
                    <span class="font-weight-medium mr-1">
                      {{ item.disputeDirection === 'in' ? 'Clock in' : 'Clock out' }}:
                    </span>
                    <span>
                      {{ disputedPunchTime(item) }}
                      <span v-if="disputedPunchDate(item)" class="grey--text">({{ disputedPunchDate(item) }})</span>
                    </span>
                  </div>

                  <div v-else>
                    <div class="d-flex align-center">
                      <v-icon x-small color="success" class="mr-1">mdi-login</v-icon>
                      <span class="mr-3">
                        {{ pairPunchTime(item, 'in') }}
                        <span v-if="disputePunchDate(item, 'in')" class="grey--text">({{ disputePunchDate(item, 'in') }})</span>
                      </span>
                      <v-icon x-small color="error" class="mr-1">mdi-logout</v-icon>
                      <span>
                        {{ pairPunchTime(item, 'out') }}
                        <span
                          v-if="disputePunchDate(item, 'out')"
                          :class="isCrossDayDispute(item) ? 'orange--text text--darken-3 font-weight-medium' : 'grey--text'"
                        >({{ disputePunchDate(item, 'out') }})</span>
                      </span>
                    </div>
                  </div>
                </template>
                <template v-slot:item.disputeStatus="{ item }">
                  <div class="d-flex align-center flex-nowrap">
                    <v-chip small :color="getDisputeStatusColor(item.disputeStatus)">
                      {{ getDisputeStatusLabel(item.disputeStatus) }}
                    </v-chip>
                    <!-- Only once a foreman has been asked. Coloured by their answer,
                         so a dispute waiting on the field can be told from one that is
                         back with payroll without opening it. -->
                    <v-tooltip top v-if="item.foremanReview">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small v-bind="attrs" v-on="on" :color="foremanReviewColor(item)" class="ml-1">
                          mdi-account-hard-hat
                        </v-icon>
                      </template>
                      <span>{{ foremanReviewTooltip(item) }}</span>
                    </v-tooltip>
                  </div>
                </template>
                <template v-slot:item.actions="{ item }">
                  <!-- flex-nowrap because "Release" is wider than "Claim": without it
                       the row grows a second line the moment a dispute is claimed,
                       and Review drops under it -->
                  <div class="d-flex align-center justify-end flex-nowrap">
                  <!-- Only on an open dispute. A resolved one needs no owner, and a
                       stale claim left on it would read as work still outstanding. -->
                  <v-tooltip top v-if="isDisputePending(item)">
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">
                        <v-btn
                          small text
                          :color="claimedByMe(item) ? 'warning' : 'primary'"
                          :disabled="claimedByOther(item) || claimingDisputeId === item.id"
                          :loading="claimingDisputeId === item.id"
                          @click.stop="claimedByMe(item) ? releaseDispute(item) : claimDispute(item)"
                        >
                          {{ claimedByMe(item) ? 'Release' : 'Claim' }}
                        </v-btn>
                      </span>
                    </template>
                    <!-- The disabled button swallows its own hover events, hence the
                         span above - without it there is no way to find out who has
                         it, which is the only reason the button is greyed out -->
                    <span>{{ claimTooltip(item) }}</span>
                  </v-tooltip>
                  <v-btn small text color="primary" @click.stop="openDisputeDialog(item)">
                    Review
                  </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>
      </template>

      <!-- MESSAGES TAB -->
      <template v-if="activeTab === 2">
        <v-row>
          <v-col cols="12">
            <v-card outlined>
              <v-card-title>
                <v-icon left>mdi-history</v-icon>
                <div>
                  Sent Messages
                  <div class="caption grey--text font-weight-regular">
                    Send a message to the employee app, for everyone or for chosen departments
                  </div>
                </div>
                <v-spacer></v-spacer>
                <v-btn color="primary" small @click="openMessageForm">
                  <v-icon left small>mdi-message-plus-outline</v-icon>
                  Send Message
                </v-btn>
                <v-btn icon small class="ml-2" @click="loadPayrollMessages" :loading="loadingMessages">
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
              </v-card-title>
              <v-card-text>
                <div v-if="loadingMessages" class="text-center py-6">
                  <v-progress-circular indeterminate color="primary"></v-progress-circular>
                </div>

                <div v-else-if="payrollMessages.length === 0" class="text-center py-6 grey--text">
                  No messages have been sent yet.
                </div>

                <v-list v-else two-line>
                  <template v-for="(message, index) in payrollMessages">
                    <v-list-item :key="message.id">
                      <v-list-item-content>
                        <v-list-item-title class="font-weight-medium">
                          {{ message.title }}
                          <v-chip v-if="message.expired" x-small class="ml-2" color="grey" text-color="white">
                            No longer shown
                          </v-chip>
                        </v-list-item-title>
                        <!-- Interpolation kept flush: with pre-wrap, the template's own
                             newlines and indentation would render as literal whitespace -->
                        <v-list-item-subtitle style="white-space: pre-wrap;">{{ message.message }}</v-list-item-subtitle>
                        <div class="caption grey--text mt-1">
                          Sent {{ formatShortDateTime(message.createdAt) }}
                          <span v-if="message.createdByName">
                            by {{ message.createdByName }}
                          </span>
                          <span>to {{ messageAudienceLabel(message) }}</span>
                          <v-tooltip bottom max-width="260">
                            <template v-slot:activator="{ on, attrs }">
                              <v-chip
                                x-small
                                class="ml-2"
                                :color="message.audienceCount === null ? 'grey lighten-1' : messageSeenColor(message)"
                                dark
                                v-on="on"
                                v-bind="attrs"
                              >
                                <template v-if="message.audienceCount === null">
                                  seen count unavailable
                                </template>
                                <template v-else>
                                  {{ message.seenCount }} of {{ message.audienceCount }} seen
                                </template>
                              </v-chip>
                            </template>
                            <span v-if="message.audienceCount === null">
                              Could not load the employee data for this count.
                            </span>
                            <span v-else>
                              Employees the app has shown this message to, out of
                              everyone with app access it was sent to.
                            </span>
                          </v-tooltip>
                        </div>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-btn
                          icon
                          small
                          color="grey"
                          :loading="deletingMessageId === message.id"
                          @click="confirmDeleteMessage(message)"
                        >
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </v-list-item-action>
                    </v-list-item>
                    <v-divider
                      v-if="index < payrollMessages.length - 1"
                      :key="'divider-' + message.id"
                    ></v-divider>
                  </template>
                </v-list>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </template>

      <!-- Sick Days Tab -->
      <template v-if="activeTab === 4">
        <v-row>
          <v-col cols="12">
            <v-card outlined>
              <v-card-title class="py-2">
                <span class="body-1 grey--text">
                  {{ pendingSickDaysCount }} pending request{{ pendingSickDaysCount !== 1 ? 's' : '' }}
                </span>
                <v-spacer></v-spacer>
                <v-text-field
                  v-model="sickDaySearch"
                  label="Filter by employee, badge or department"
                  prepend-inner-icon="mdi-magnify"
                  clearable
                  dense
                  outlined
                  hide-details
                  class="mr-3"
                  style="max-width: 360px"
                ></v-text-field>
                <v-btn-toggle v-model="sickDayFilter" dense mandatory>
                  <v-btn small value="pending">
                    Pending
                  </v-btn>
                  <v-btn small value="resolved">
                    Resolved
                  </v-btn>
                  <v-btn small value="all">
                    All
                  </v-btn>
                </v-btn-toggle>
                <v-btn icon small class="ml-2" @click="loadSickDayRequests" :loading="loadingSickDays">
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
              </v-card-title>
              <v-card-text v-if="sickDaysError" class="pb-0">
                <v-alert type="warning" dense text class="mb-0">
                  {{ sickDaysError }}
                </v-alert>
              </v-card-text>
              <v-data-table
                :headers="sickDayHeaders"
                :items="filteredSickDays"
                :loading="loadingSickDays"
                :items-per-page="15"
                sort-by="requestedAt"
                sort-desc
                class="elevation-0"
                :no-data-text="sickDayFilter === 'pending' ? 'No pending sick day requests' : 'No sick day requests found'"
              >
                <template v-slot:item.department="{ item }">
                  <span v-if="item.department">{{ item.department }}</span>
                  <span v-else class="grey--text">-</span>
                </template>
                <template v-slot:item.requestedDate="{ item }">
                  {{ formatShiftDate(item.requestedDate) }}
                </template>
                <template v-slot:item.requestedAt="{ item }">
                  {{ formatShortDateTime(item.requestedAt) }}
                </template>
                <template v-slot:item.status="{ item }">
                  <v-chip small :color="getStatusChipColor(item.status)" dark>
                    {{ getStatusText(item.status) }}
                  </v-chip>
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-btn small text color="primary" @click.stop="openSickDayDialog(item)">
                    Review
                  </v-btn>
                </template>
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>
      </template>
    </template>

    <!-- Dispute Resolution Dialog (Clock-out Disputes) -->
    <v-dialog v-model="disputeDialogOpen" max-width="600" scrollable>
      <v-card v-if="selectedDispute">
        <v-card-title class="d-flex align-center">
          <v-icon left color="warning">mdi-alert-circle</v-icon>
          Dispute Review
          <v-spacer></v-spacer>
          <v-chip :color="getDisputeStatusColor(selectedDispute.disputeStatus)">
            {{ getDisputeStatusLabel(selectedDispute.disputeStatus) }}
          </v-chip>
          <v-btn icon @click="disputeDialogOpen = false" class="ml-2">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text class="pt-4">
          <!-- Employee, then date and job site -->
          <v-row>
            <v-col cols="12" class="pb-0">
              <div class="caption grey--text">Employee</div>
              <div class="text-h6">
                {{ selectedDispute.employeeName }}
                <span v-if="selectedDispute.badgeNumber && selectedDispute.badgeNumber !== '-'" class="body-2 grey--text">
                  &middot; Badge {{ selectedDispute.badgeNumber }}
                </span>
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <!-- "Reported Time", not "Dispute Time": this is createdAt, the moment
                   the employee raised it. Sitting beside Punch Time, which is the
                   punch itself, the old label did not say which of the two it was. -->
              <div class="caption grey--text">Reported Time</div>
              <div>{{ formatDateTimeFull(selectedDispute.createdAt) }}</div>
            </v-col>
            <v-col cols="6">
              <div class="caption grey--text">Job Site</div>
              <div>{{ selectedDispute.jobSiteName || 'Unknown' }}</div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <!-- The day worked, which is not always the day the punch happened -
                   a forgotten punch out closed the next morning files under the
                   shift it belongs to -->
              <div class="caption grey--text">Shift Date</div>
              <div>{{ formatShiftDate(selectedDispute.date) }}</div>
            </v-col>
            <v-col cols="6">
              <!-- The hours they were due to work, so a claimed time can be judged
                   against the shift. The punch itself is in the card below. -->
              <div class="caption grey--text">Scheduled Shift</div>
              <div v-if="shiftWindow(selectedDispute)">{{ shiftWindow(selectedDispute) }}</div>
              <!-- Explicitly null means the punch endpoint looked and found no
                   schedule for that weekday - they worked a day they were not on.
                   Field missing entirely means the dispute predates this being
                   recorded, which is a different thing and must not read as the
                   first. -->
              <div v-else-if="selectedDispute.shiftStartTime === null" class="grey--text">
                Not scheduled that day
              </div>
              <div v-else class="grey--text">Not recorded</div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <!-- Both on a legacy pair record, where one dispute covered an in and
                   an out - naming one and dropping the other would hide half of what
                   the dispute points at -->
              <div class="caption grey--text">Punch Record Direction</div>
              <div v-if="punchRecordDirection(selectedDispute)">
                {{ punchRecordDirection(selectedDispute) }}
              </div>
              <div v-else class="red--text font-weight-medium">Punch Record Not Found</div>
            </v-col>
            <v-col cols="6">
              <!-- The punch record as it stands NOW, which is not necessarily what
                   the app filed - the card below holds that. Keeping them in separate
                   places is what lets payroll see a correction without losing sight
                   of the time being disputed. -->
              <div class="caption grey--text">Punch Record Time</div>
              <div v-if="punchRecordTime(selectedDispute)" class="d-flex align-center">
                <span>{{ punchRecordTime(selectedDispute) }}</span>
                <!-- Same wand Payroll marks an edited punch with, so the two pages
                     say the same thing about the same record. It means the time
                     beside it is no longer what the app filed - the card below still
                     holds that.
                     Before the pencil: it describes the value just read, while the
                     pencil acts on it, so the control stays last. -->
                <v-tooltip top v-if="punchRecordEdited(selectedDispute)">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon small v-bind="attrs" v-on="on" class="orange--text ml-1">auto_fix_high</v-icon>
                  </template>
                  <span>This punch record has been edited</span>
                </v-tooltip>
                <v-btn
                  v-if="canCorrectPunch(selectedDispute, selectedDispute.disputeDirection)"
                  icon x-small class="ml-1"
                  title="Edit punch record"
                  @click="openPunchCorrection(selectedDispute, selectedDispute.disputeDirection)"
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </div>
              <!-- Same wording and colour as the direction beside it: one missing
                   record is the cause of both, so two different messages would read
                   as two different problems -->
              <div v-else class="red--text font-weight-medium">Punch Record Not Found</div>
            </v-col>
          </v-row>

          <!-- Punch Times -->
          <v-card outlined class="mt-4">
            <v-card-text class="py-3">
              <div class="caption grey--text mb-2">
                {{ selectedDispute.isLegacyPair ? 'Punch Times' : 'Disputed Punch' }}
              </div>

              <!-- One punch. The pair below is only for records written before a
                   dispute belonged to a single punch. -->
              <div v-if="!selectedDispute.isLegacyPair" class="d-flex align-center mb-2">
                <v-icon :color="selectedDispute.disputeDirection === 'in' ? 'success' : 'error'" class="mr-1">
                  {{ selectedDispute.disputeDirection === 'in' ? 'mdi-login' : 'mdi-logout' }}
                </v-icon>
                <strong class="mr-1">
                  {{ selectedDispute.disputeDirection === 'in' ? 'Clock in' : 'Clock out' }}:
                </strong>
                {{ disputedPunchTime(selectedDispute) }}
                <span v-if="disputedPunchDate(selectedDispute)" class="grey--text ml-1">
                  ({{ disputedPunchDate(selectedDispute) }})
                </span>
              </div>

              <!-- What the employee says it should have been. Only present when they
                   chose "this punch time is wrong" and gave a usable value, so its
                   absence is meaningful: on a forgotten punch out they confirmed the
                   recorded time was right. The sentence in the reason below still
                   carries it too, for disputes filed before it was a field. -->
              <div v-if="selectedDispute.claimedPunchAt" class="d-flex align-center mb-2 ml-1">
                <!-- No size prop, matching the punch icon above it: these are two
                     lines of the same kind, so one should not read as a footnote -->
                <v-icon color="warning" class="mr-1">mdi-chat-outline</v-icon>
                <strong class="mr-1">Claimed:</strong>
                <!-- Same shape as the punch above it - time then (M/D) - so the two
                     can be compared at a glance instead of read -->
                {{ formatPunchTime(selectedDispute.claimedPunchAt) }}
                <span class="grey--text ml-1">
                  ({{ formatPunchDate(selectedDispute.claimedPunchAt) }})
                </span>
              </div>

              <!-- Actual Times (primary - dark) -->
              <div v-if="selectedDispute.isLegacyPair" class="d-flex align-center mb-2">
                <div class="mr-6">
                  <v-icon color="success" class="mr-1">mdi-login</v-icon>
                  <strong>In:</strong> {{ pairPunchTime(selectedDispute, 'in') }}
                  <span v-if="disputePunchDate(selectedDispute, 'in')" class="grey--text">
                    ({{ disputePunchDate(selectedDispute, 'in') }})
                  </span>
                </div>
                <div>
                  <v-icon color="error" class="mr-1">mdi-logout</v-icon>
                  <strong>Out:</strong> {{ pairPunchTime(selectedDispute, 'out') }}
                  <!-- Dates always shown, not just on cross-day disputes: 9:46 AM to
                       10:00 AM reads as fourteen minutes unless the day is on screen.
                       A different day is coloured so it cannot be skimmed past. -->
                  <span
                    v-if="disputePunchDate(selectedDispute, 'out')"
                    :class="isCrossDayDispute(selectedDispute) ? 'orange--text text--darken-3 font-weight-medium' : 'grey--text'"
                  >
                    ({{ disputePunchDate(selectedDispute, 'out') }})
                  </span>
                </div>
              </div>

              <!-- Rounded Times (secondary - light, only if different from actual) -->
            </v-card-text>
          </v-card>

          <!-- Overtime reason, recorded on the punch itself when the employee clocked
               out past their scheduled end. Separate from the dispute reason below. -->
          <div v-if="selectedDispute.lateClockOutReason" class="mt-4">
            <div class="caption grey--text mb-1">Overtime Reason</div>
            <v-alert dense text color="deep-orange darken-1" class="mb-0">
              <div class="d-flex align-start">
                <v-icon small color="deep-orange darken-1" class="mr-2 mt-1">mdi-clock-alert-outline</v-icon>
                <div class="body-2" style="white-space: pre-wrap;">{{ selectedDispute.lateClockOutReason }}</div>
              </div>
            </v-alert>
          </div>

          <!-- Employee's Dispute Reason. Hidden entirely when there is none: a
               forgotten punch out where the employee confirmed the time was asked
               for no explanation, and their overtime reason is shown in its own
               block below. An empty card would read as a question they ignored. -->
          <div class="mt-4" v-if="selectedDispute.disputeReason">
            <div class="caption grey--text mb-2">Employee's Dispute Reason</div>
            <v-card outlined color="warning lighten-5">
              <v-card-text class="py-3">
                <div class="d-flex align-start">
                  <v-icon color="warning" class="mr-2 mt-1">mdi-comment-alert</v-icon>
                  <!-- Shown exactly as stored. The employee app writes only the
                       employee's words, and the older single-direction records have
                       been cleaned to match, so there is nothing left to parse out
                       at display time. Legacy pair records still hold assembled
                       prose and are shown as they are - that text is the only place
                       their claimed time and shift context exist. -->
                  <div class="body-1" style="white-space: pre-wrap;">{{ selectedDispute.disputeReason }}</div>
                </div>
              </v-card-text>
            </v-card>
          </div>

          <!-- Foreman Review. The foreman's answer is evidence for payroll's
               decision, not the decision itself - Approve / Not Approved below stay
               with whoever has claimed the dispute. -->
          <div class="mt-4" v-if="selectedDispute.foremanReview || canActionDispute(selectedDispute)">
            <div class="caption grey--text mb-2">Foreman Review</div>
            <v-card v-if="selectedDispute.foremanReview" outlined>
              <v-card-text class="py-3">
                <div class="d-flex align-center mb-2">
                  <v-icon small class="mr-1">mdi-account-hard-hat</v-icon>
                  <strong>{{ foremanName(selectedDispute) }}</strong>
                  <span class="caption grey--text ml-2">
                    asked {{ formatDateTime(selectedDispute.foremanReview.requestedAt) }}
                  </span>
                  <v-spacer></v-spacer>
                  <v-chip small :color="foremanReviewColor(selectedDispute)" dark>
                    {{ foremanReviewLabel(selectedDispute) }}
                  </v-chip>
                </div>
                <div class="body-2" style="white-space: pre-wrap;"><strong>Asked:</strong> {{ selectedDispute.foremanReview.question }}</div>
                <template v-if="selectedDispute.foremanReview.respondedAt">
                  <div class="body-2 mt-2" style="white-space: pre-wrap;"><strong>Answer:</strong> {{ selectedDispute.foremanReview.notes }}</div>
                  <div class="caption grey--text mt-1">
                    Answered {{ formatDateTime(selectedDispute.foremanReview.respondedAt) }}
                  </div>
                </template>
              </v-card-text>
              <!-- Only before an answer: withdrawing deletes the request, and with it
                   anything the foreman had said -->
              <v-card-actions v-if="canWithdrawForemanAsk(selectedDispute)" class="pt-0">
                <v-spacer></v-spacer>
                <v-btn small text color="warning" :loading="withdrawingForemanAsk" @click="withdrawForemanAsk">
                  Withdraw
                </v-btn>
              </v-card-actions>
            </v-card>
            <v-btn v-else small outlined color="primary" @click="openAskForeman(selectedDispute)">
              <v-icon left small>mdi-account-hard-hat</v-icon>
              Ask Foreman
            </v-btn>
          </div>

          <!-- Existing Resolution Notes -->
          <div class="mt-4" v-if="selectedDispute.disputeResolutionNotes && selectedDispute.disputeStatus !== 'pending'">
            <div class="caption grey--text">Payroll Resolution</div>
            <v-alert :type="selectedDispute.disputeStatus === 'approved' ? 'success' : 'error'" dense class="mt-2">
              <div class="font-italic">{{ selectedDispute.disputeResolutionNotes }}</div>
              <div class="caption mt-2">
                Resolved by {{ selectedDispute.disputeResolvedByName || selectedDispute.disputeResolvedBy }}
                on {{ formatDateTime(selectedDispute.disputeResolvedAt) }}
              </div>
            </v-alert>
          </div>

          <!-- Resolution Input (for pending disputes) -->
          <div class="mt-4" v-if="!selectedDispute.disputeStatus || selectedDispute.disputeStatus === 'pending'">
            <v-textarea
              v-model="disputeResolutionNotes"
              label="Payroll Notes (required for resolution)"
              rows="3"
              outlined
              dense
              :disabled="!canActionDispute(selectedDispute)"
              placeholder="Explain the resolution decision..."
            ></v-textarea>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <!-- Beside the buttons they explain, rather than as a banner further up:
               a greyed-out Approve needs its reason within reading distance -->
          <span v-if="needsClaim(selectedDispute)" class="caption grey--text">
            Claim to action
          </span>
          <span v-else-if="claimedByOther(selectedDispute)" class="caption grey--text">
            Claimed by {{ claimedByName(selectedDispute) }}
          </span>
          <v-spacer></v-spacer>
          <template v-if="!selectedDispute.disputeStatus || selectedDispute.disputeStatus === 'pending'">
            <v-btn
              color="orange"
              outlined
              @click="resolveDispute('closed')"
              :loading="actionLoading"
              :disabled="!disputeResolutionNotes.trim() || !canActionDispute(selectedDispute)"
            >
              <v-icon left small>mdi-close-circle-outline</v-icon>
              Not Approved
            </v-btn>
            <v-btn
              color="success"
              @click="resolveDispute('approved')"
              :loading="actionLoading"
              :disabled="!disputeResolutionNotes.trim() || !canActionDispute(selectedDispute)"
              class="ml-2"
            >
              <v-icon left small>mdi-check</v-icon>
              Approve
            </v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Ask Foreman -->
    <v-dialog v-model="askForemanDialog" max-width="520" persistent>
      <v-card v-if="selectedDispute">
        <v-card-title primary-title>Ask Foreman</v-card-title>
        <v-card-text>
          <p class="body-2 mb-4">
            Sends this dispute for <strong>{{ selectedDispute.employeeName }}</strong> to a
            foreman or supervisor. The decision stays with you.
          </p>
          <v-autocomplete
            v-model="askForemanId"
            :items="askForemanItems"
            :loading="loadingAskForeman"
            item-text="name"
            item-value="id"
            label="Foreman or supervisor"
            no-data-text="No foremen or supervisors are registered in the employee app"
            outlined
            dense
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.name }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ item.department }}<span v-if="item.scheduled"> &middot; Scheduled on this job that day</span>
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
          <v-textarea
            v-model="askForemanQuestion"
            label="Question for the foreman"
            placeholder="e.g. Was he still on site at 5:30?"
            rows="3"
            outlined
            dense
            hide-details
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text :disabled="sendingForemanAsk" @click="askForemanDialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            :loading="sendingForemanAsk"
            :disabled="sendingForemanAsk || !askForemanId || !askForemanQuestion.trim()"
            @click="sendForemanAsk"
          >
            Send
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Punch Record -->
    <v-dialog v-model="punchCorrectionDialog" max-width="520" persistent>
      <v-card v-if="punchToCorrect">
        <v-card-title primary-title>Edit Punch Record</v-card-title>
        <v-card-text>
          <p class="body-2 mb-4">
            Sets a new date and time on the
            {{ punchToCorrect.direction === 'in' ? 'clock in' : 'clock out' }}
            record for <strong>{{ punchToCorrect.employeeName }}</strong>. The dispute
            keeps the time the app originally filed.
          </p>

          <v-menu v-model="correctionDateMenu" :close-on-content-click="false" offset-y min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                label="New Punch Date"
                v-model="correctionDate"
                prepend-icon="mdi-calendar"
                readonly outlined hide-details
                v-bind="attrs" v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker v-model="correctionDate" @input="correctionDateMenu = false"></v-date-picker>
          </v-menu>
          <br />
          <v-text-field
            label="New Punch Time"
            v-model="correctionTime"
            type="time"
            prepend-icon="mdi-clock"
            outlined
            hide-details
          ></v-text-field>

          <v-alert v-if="correctionIsUnchanged" type="warning" dense text class="mt-4 mb-0">
            That is the time already on the record.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text :disabled="savingPunchCorrection" @click="punchCorrectionDialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            :loading="savingPunchCorrection"
            :disabled="savingPunchCorrection || !correctionDate || !correctionTime || correctionIsUnchanged"
            @click="savePunchCorrection"
          >
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Uploads Tab -->
    <template v-if="activeTab === 3">
      <v-row>
        <v-col cols="12">
          <v-card outlined>
            <v-card-title class="subtitle-1">
              Employee Documents
              <v-spacer></v-spacer>
              <v-btn small text color="primary" :loading="loadingUploads" @click="loadUploads">
                <v-icon left small>mdi-refresh</v-icon>
                Refresh
              </v-btn>
            </v-card-title>
            <v-card-subtitle>
              Uploads by employees via the employee app.
            </v-card-subtitle>
            <v-card-text>
              <v-row dense class="mb-2">
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model="uploadSearch"
                    label="Filter by employee or badge"
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    dense
                    outlined
                    hide-details
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-select
                    v-model="uploadTypeFilter"
                    :items="uploadTypeOptions"
                    label="Document type"
                    dense
                    outlined
                    hide-details
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-select
                    v-model="uploadCheckFilter"
                    :items="uploadCheckOptions"
                    label="Expiration check"
                    dense
                    outlined
                    hide-details
                  ></v-select>
                </v-col>
              </v-row>

              <!-- Says how many are flagged whatever the filter is set to, so the
                   backlog is visible without having to go looking for it -->
              <div v-if="mismatchUploadCount" class="caption mb-3">
                <v-icon x-small color="warning" class="mr-1">mdi-alert-circle-outline</v-icon>
                {{ mismatchUploadCount }}
                {{ mismatchUploadCount === 1 ? "document is" : "documents are" }}
                flagged for review.
                <a
                  v-if="uploadCheckFilter !== 'mismatch'"
                  href="#"
                  @click.prevent="uploadCheckFilter = 'mismatch'"
                >Show only those</a>
                <a v-else href="#" @click.prevent="uploadCheckFilter = 'all'">Show all</a>
              </div>

              <v-alert v-if="uploadsError" type="warning" dense text class="mb-3">
                {{ uploadsError }}
              </v-alert>

              <v-data-table
                :headers="uploadHeaders"
                :items="filteredUploads"
                :loading="loadingUploads"
                :items-per-page="25"
                :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
                sort-by="uploadedAt"
                sort-desc
                class="elevation-0"
                no-data-text="No uploads found"
              >
                <template v-slot:item.uploadedAt="{ item }">
                  {{ formatDateTimeFull(item.uploadedAt) }}
                </template>
                <template v-slot:item.expirationDate="{ item }">
                  <span v-if="!item.expirationDate" class="grey--text">-</span>
                  <span v-else :class="item.expired ? 'red--text font-weight-medium' : ''">
                    {{ formatExpiry(item.expirationDate) }}
                  </span>
                  <v-btn icon x-small class="ml-1" title="Edit expiration" @click="openEditExpiration(item)">
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                  <!-- Only a mismatch is marked. A tick on almost every row would be
                       noise, and the point is to make the exceptions findable. A
                       Vision failure is recorded as "error" and shows nothing - it
                       means unverified, not wrong. -->
                  <v-tooltip bottom max-width="320">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        v-if="item.expirationCheck && item.expirationCheck.status === 'mismatch'"
                        v-bind="attrs"
                        v-on="on"
                        small
                        color="warning"
                        class="ml-1"
                      >mdi-alert-circle-outline</v-icon>
                    </template>
                    <span>{{ expirationCheckTooltip(item) }}</span>
                  </v-tooltip>
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-btn small text color="primary" :href="item.url" target="_blank" rel="noopener">
                    <v-icon left small>mdi-open-in-new</v-icon>
                    View
                  </v-btn>
                  <v-btn icon small color="error" title="Delete" @click="confirmDeleteUpload(item)">
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <!-- Delete Upload Confirmation -->
    <v-dialog v-model="deleteUploadDialog" max-width="500" persistent>
      <v-card v-if="uploadToDelete">
        <v-card-title class="text-h6">
          Delete this document?
        </v-card-title>
        <v-card-text>
          <p class="body-2 mb-3">
            This permanently deletes <strong>{{ uploadToDelete.typeLabel }}</strong> for
            <strong>{{ uploadToDelete.employeeName }}</strong>, including the uploaded file.
            It cannot be undone.
          </p>
          <v-alert type="warning" dense text class="mb-0">
            They will not be able to punch in or out until they upload a replacement.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="deletingUpload" @click="deleteUploadDialog = false">
            Cancel
          </v-btn>
          <v-btn color="error" :loading="deletingUpload" @click="deleteUpload">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Expiration -->
    <v-dialog v-model="editExpirationDialog" max-width="560" persistent>
      <v-card v-if="uploadToEdit">
        <v-card-title class="text-h6">
          Edit expiration
          <v-spacer></v-spacer>
          <v-btn icon :disabled="savingExpiration" @click="editExpirationDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <p class="body-2 mb-4">
            <strong>{{ uploadToEdit.typeLabel }}</strong> for
            <strong>{{ uploadToEdit.employeeName }}</strong>
            <span v-if="uploadToEdit.badgeNumber && uploadToEdit.badgeNumber !== '-'" class="grey--text">
              &middot; Badge {{ uploadToEdit.badgeNumber }}
            </span>
          </p>

          <!-- For reference only. These include issue dates and dates of birth, so
               they are not a list of candidate expiries to pick from. -->
          <div v-if="editDatesFound.length" class="mb-4">
            <div class="caption grey--text mb-1">Dates read from the document</div>
            <div class="body-2">{{ editDatesFoundLabel }}</div>
          </div>
          <v-alert v-else type="info" dense text class="mb-4">
            No dates could be read from this image, so there is nothing to compare against.
            Open the document to check it by hand.
          </v-alert>

          <v-menu
            v-model="editExpirationMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="editExpirationDate"
                label="Expiration Date"
                prepend-icon="mdi-calendar"
                readonly
                outlined
                dense
                hide-details
                class="mb-4"
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="editExpirationDate"
              @input="editExpirationMenu = false"
            ></v-date-picker>
          </v-menu>

          <v-select
            v-model="editExpirationStatus"
            :items="expirationStatusOptions"
            label="Check status"
            prepend-icon="mdi-shield-check-outline"
            outlined
            dense
            hide-details
            class="mb-2"
          ></v-select>
          <div class="caption grey--text">
            Only "Flagged for review" shows a warning in this table.
          </div>

          <v-alert v-if="editExpirationWillUnexpire" type="warning" dense text class="mt-4 mb-0">
            This date is in the past, so they will not be able to punch until it is
            corrected or they upload a replacement.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-btn text color="primary" :href="uploadToEdit.url" target="_blank" rel="noopener">
            <v-icon left small>mdi-open-in-new</v-icon>
            View document
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text :disabled="savingExpiration" @click="editExpirationDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :loading="savingExpiration"
            :disabled="!editExpirationDate || !editExpirationStatus"
            @click="saveExpirationEdit"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Timesheet Detail Dialog -->
    <v-dialog v-model="dialogOpen" max-width="700" scrollable>
      <v-card v-if="selectedTimesheet">
        <v-card-title class="d-flex align-center">
          <v-icon left>mdi-account-clock</v-icon>
          {{ selectedTimesheet.employeeName }}
          <span v-if="selectedTimesheet.badgeNumber && selectedTimesheet.badgeNumber !== '-'" class="body-2 grey--text ml-2">
            &middot; Badge {{ selectedTimesheet.badgeNumber }}
          </span>
          <v-spacer></v-spacer>
          <v-chip :color="getStatusChipColor(selectedTimesheet.status)" dark>
            {{ getStatusText(selectedTimesheet.status) }}
          </v-chip>
          <v-btn icon @click="dialogOpen = false" class="ml-2">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text class="pt-4">
          <!-- Week Info -->
          <v-row>
            <v-col cols="6">
              <div class="caption grey--text">Week Period</div>
              <div>{{ formatWeekRange(selectedTimesheet.weekStart, selectedTimesheet.weekEnd) }}</div>
            </v-col>
            <v-col cols="6">
              <div class="caption grey--text">Total Hours</div>
              <div class="text-h5 primary--text">{{ formatHours(selectedTimesheet.weeklyTotal) }}</div>
            </v-col>
          </v-row>

          <v-row v-if="selectedTimesheet.department">
            <v-col cols="12">
              <div class="caption grey--text">Department</div>
              <div>{{ selectedTimesheet.department }}</div>
            </v-col>
          </v-row>

          <!-- Unseen Changes Alert -->
          <v-alert
            v-if="hasUnseenChanges"
            type="warning"
            outlined
            class="mt-4"
          >
            <div class="font-weight-bold mb-2">
              <v-icon left small color="warning">mdi-alert</v-icon>
              Timesheet Changed Since Employee Signed
            </div>
            <div class="caption mb-2">
              Punches, meals or dispute decisions have changed since the employee signed
              this timesheet, so it no longer matches what they signed. Request a re-sign
              before approving.
            </div>
            <v-list dense class="pa-0" style="background: transparent;">
              <v-list-item v-for="(change, idx) in unseenChanges" :key="idx" class="px-0">
                <v-list-item-icon class="mr-2">
                  <v-icon small color="warning">mdi-clock-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="body-2">
                    <strong>{{ change.dayLabel }}</strong>: {{ change.description }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="caption">
                    <span class="text-decoration-line-through error--text">{{ change.oldValue }}</span>
                    <v-icon x-small class="mx-1">mdi-arrow-right</v-icon>
                    <span class="success--text font-weight-medium">{{ change.newValue }}</span>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-alert>

          <!-- Loading current punch data indicator -->
          <div v-if="loadingCurrentPunches" class="mt-4 text-center">
            <v-progress-circular indeterminate color="primary" size="24"></v-progress-circular>
            <span class="ml-2 caption grey--text">Checking for changes...</span>
          </div>

          <!-- Daily Records -->
          <div class="mt-4" v-if="selectedTimesheet.dailyRecords && selectedTimesheet.dailyRecords.length">
            <div class="caption grey--text mb-2">Daily Breakdown</div>
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Job Site</th>
                    <th class="text-right">Hours</th>
                    <th class="text-center" style="width: 110px;">Status</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="day in selectedTimesheet.dailyRecords">
                    <tr :key="day.date" :class="{ 'warning lighten-5': day.disputed }">
                      <td>{{ formatDate(day.date) }}</td>
                      <td>{{ day.jobSiteName || '-' }}</td>
                      <td class="text-right">{{ formatHours(day.hoursWorked) }}</td>
                      <td class="text-center">
                        <v-chip
                          v-for="(dispute, idx) in dayDisputes(day)"
                          :key="'chip-' + idx"
                          x-small
                          dark
                          :color="getDisputeStatusColor(dispute.status)"
                          :class="{ 'mt-1': idx > 0 }"
                        >
                          <span v-if="dispute.direction" class="mr-1">
                            {{ dispute.direction === 'in' ? 'IN' : 'OUT' }}
                          </span>
                          {{ getDisputeStatusText(dispute.status) }}
                        </v-chip>
                        <!-- Scannable marker so OT days are visible without reading every row -->
                        <v-chip
                          v-if="day.lateClockOutReason"
                          x-small
                          color="deep-orange darken-1"
                          dark
                          :class="{ 'mt-1': day.disputed }"
                        >
                          OT
                        </v-chip>
                      </td>
                    </tr>
                    <tr v-if="day.lateClockOutReason" :key="day.date + '-overtime'" class="amber lighten-5">
                      <td colspan="4" class="py-2 pl-6">
                        <div class="d-flex align-start">
                          <v-icon small color="deep-orange darken-1" class="mr-2 mt-1">mdi-clock-alert-outline</v-icon>
                          <div>
                            <span class="caption grey--text">Overtime Reason:</span>
                            <div class="body-2" style="white-space: pre-wrap;">{{ day.lateClockOutReason }}</div>
                          </div>
                        </div>
                      </td>
                    </tr>
                    <!-- One pair of rows per dispute: a day can carry one on its
                         clock in and one on its clock out, each resolved separately -->
                    <template v-for="(dispute, idx) in dayDisputes(day)">
                      <tr v-if="dispute.reason" :key="day.date + '-comment-' + idx" class="warning lighten-5">
                        <td colspan="4" class="py-2 pl-6">
                          <div class="d-flex align-start">
                            <v-icon small color="warning" class="mr-2 mt-1">mdi-comment-alert</v-icon>
                            <div>
                              <span class="caption grey--text">
                                Employee Comment<span v-if="dispute.direction"> ({{ dispute.direction === 'in' ? 'clock in' : 'clock out' }})</span>:
                              </span>
                              <div class="body-2">{{ dispute.reason }}</div>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr
                        v-if="dispute.resolutionNotes"
                        :key="day.date + '-resolution-' + idx"
                        :class="dispute.status === 'approved' ? 'success lighten-5' : 'orange lighten-5'"
                      >
                        <td colspan="4" class="py-2 pl-6">
                          <div class="d-flex align-start">
                            <v-icon small :color="getDisputeStatusColor(dispute.status)" class="mr-2 mt-1">mdi-check-circle</v-icon>
                            <div>
                              <span class="caption grey--text">Payroll Response:</span>
                              <div class="body-2">{{ dispute.resolutionNotes }}</div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </template>
                  </template>
                </tbody>
              </template>
            </v-simple-table>
          </div>

          <!-- Signature -->
          <div class="mt-4" v-if="selectedTimesheet.signature">
            <div class="caption grey--text mb-1">Employee Signature</div>
            <img
              :src="getSignatureUrl(selectedTimesheet.signature)"
              alt="Signature"
              style="max-width: 200px; border: 1px solid #ccc; border-radius: 4px;"
              @error="$event.target.style.display='none'"
            />
          </div>

          <!-- Submitted Info -->
          <v-row class="mt-4">
            <v-col cols="6">
              <div class="caption grey--text">Submitted</div>
              <div>{{ formatDateTime(selectedTimesheet.submittedAt) }}</div>
            </v-col>
            <v-col cols="6" v-if="selectedTimesheet.reviewedAt">
              <div class="caption grey--text">Reviewed By</div>
              <div>{{ selectedTimesheet.reviewedByName || selectedTimesheet.reviewedBy }}</div>
              <div class="caption">{{ formatDateTime(selectedTimesheet.reviewedAt) }}</div>
            </v-col>
          </v-row>

          <!-- Employee's Notes (their own words, submitted with the timesheet) -->
          <div class="mt-4" v-if="selectedTimesheet.employeeNotes">
            <div class="caption grey--text mb-2">Notes From Employee</div>
            <v-card outlined color="blue lighten-5">
              <v-card-text class="py-3">
                <div class="d-flex align-start">
                  <v-icon color="primary" class="mr-2 mt-1">mdi-comment-text-outline</v-icon>
                  <div class="body-1" style="white-space: pre-wrap;">{{ selectedTimesheet.employeeNotes }}</div>
                </div>
              </v-card-text>
            </v-card>
          </div>

          <!-- Existing Notes Display -->
          <div class="mt-4" v-if="selectedTimesheet.internalNotes || selectedTimesheet.reviewNotes">
            <div v-if="selectedTimesheet.internalNotes" class="mb-3">
              <div class="caption grey--text">Internal Notes (Not shown to employee)</div>
              <div class="font-italic">{{ selectedTimesheet.internalNotes }}</div>
            </div>
            <div v-if="selectedTimesheet.reviewNotes">
              <div class="caption grey--text">Notes to Employee</div>
              <div class="font-italic">{{ selectedTimesheet.reviewNotes }}</div>
            </div>
          </div>

          <!-- Notes Input (for pending timesheets or re-confirmation requests) -->
          <div class="mt-4" v-if="selectedTimesheet.status !== 'approved'">
            <v-textarea
              v-model="internalNotes"
              label="Internal Notes (optional)"
              rows="2"
              outlined
              dense
              hide-details
              placeholder="Internal notes - not shown to employee..."
            ></v-textarea>
            <v-textarea
              v-model="reviewNotes"
              :label="selectedTimesheet.status === 'needs_reconfirmation' ? 'Notes to Employee (optional)' : 'Notes to Employee (required for re-sign request)'"
              rows="2"
              outlined
              dense
              hide-details
              class="mt-3"
              :placeholder="selectedTimesheet.status === 'needs_reconfirmation' ? '' : 'Explain what was changed and why they need to re-sign...'"
            ></v-textarea>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-btn text color="primary" @click="openPdf(selectedTimesheet)" v-if="selectedTimesheet.pdfUrl">
            <v-icon left small>mdi-file-pdf-box</v-icon>
            View PDF
          </v-btn>
          <v-spacer></v-spacer>
          <template v-if="selectedTimesheet.status !== 'approved'">
            <v-btn
              color="warning"
              outlined
              @click="requestReconfirmation"
              :loading="actionLoading"
              :disabled="!reviewNotes.trim()"
            >
              <v-icon left small>mdi-signature-freehand</v-icon>
              Request Re-sign
            </v-btn>
            <v-tooltip top :disabled="!hasUnseenChanges">
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  <v-btn
                    color="success"
                    @click="approveTimesheet"
                    :loading="actionLoading"
                    :disabled="hasUnseenChanges"
                    class="ml-2"
                  >
                    <v-icon left small>mdi-check</v-icon>
                    Approve
                  </v-btn>
                </span>
              </template>
              <span>Punch data has changed. Request re-sign first.</span>
            </v-tooltip>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Sick Day Review -->
    <v-dialog v-model="sickDayDialogOpen" max-width="520" :persistent="!!resolvingSickDay">
      <v-card v-if="selectedSickDay">
        <v-card-title class="d-flex align-center">
          <v-icon left color="primary">mdi-emoticon-sick-outline</v-icon>
          Sick Day Request
          <v-spacer></v-spacer>
          <v-chip :color="getStatusChipColor(selectedSickDay.status)" dark>
            {{ getStatusText(selectedSickDay.status) }}
          </v-chip>
          <v-btn icon class="ml-2" :disabled="!!resolvingSickDay" @click="sickDayDialogOpen = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text class="pt-4">
          <div class="caption grey--text">Employee</div>
          <div class="text-h6">
            {{ selectedSickDay.employeeName }}
            <span v-if="selectedSickDay.badgeNumber && selectedSickDay.badgeNumber !== '-'" class="body-2 grey--text">
              &middot; Badge {{ selectedSickDay.badgeNumber }}
            </span>
          </div>
          <v-row>
            <v-col cols="6">
              <div class="caption grey--text">Sick Date</div>
              <div>{{ formatShiftDate(selectedSickDay.requestedDate) }}</div>
            </v-col>
            <v-col cols="6">
              <div class="caption grey--text">Requested</div>
              <div>{{ formatDateTimeFull(selectedSickDay.requestedAt) }}</div>
            </v-col>
          </v-row>
          <v-row v-if="selectedSickDay.department" class="mt-0">
            <v-col cols="12" class="pt-0">
              <div class="caption grey--text">Department</div>
              <div>{{ selectedSickDay.department }}</div>
            </v-col>
          </v-row>

          <!-- Decided: show what was said and by whom. Approving needs no note, so an
               approval can arrive here with none. -->
          <v-alert
            v-if="selectedSickDay.status !== 'pending'"
            :type="selectedSickDay.status === 'approved' ? 'success' : 'error'"
            dense
            class="mt-4 mb-0"
          >
            <div v-if="selectedSickDay.resolutionNotes" class="font-italic" style="white-space: pre-wrap;">{{ selectedSickDay.resolutionNotes }}</div>
            <div :class="{ 'caption mt-2': selectedSickDay.resolutionNotes }">
              {{ getStatusText(selectedSickDay.status) }} by {{ selectedSickDay.resolvedByName || 'payroll' }}
              on {{ formatDateTimeFull(selectedSickDay.resolvedAt) }}
            </div>
          </v-alert>

          <!-- Required to deny, optional to approve: a denial is the one the employee
               needs a reason for -->
          <v-textarea
            v-else
            v-model="sickDayNotes"
            label="Note to employee"
            hint="Required to deny"
            persistent-hint
            rows="3"
            outlined
            dense
            class="mt-4"
            :disabled="!!resolvingSickDay"
          ></v-textarea>
        </v-card-text>

        <template v-if="selectedSickDay.status === 'pending'">
          <v-divider></v-divider>
          <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <v-btn
              color="error"
              outlined
              :loading="resolvingSickDay === 'denied'"
              :disabled="!sickDayNotes.trim() || !!resolvingSickDay"
              @click="resolveSickDay('denied')"
            >
              <v-icon left small>mdi-close-circle-outline</v-icon>
              Deny
            </v-btn>
            <v-btn
              color="success"
              class="ml-2"
              :loading="resolvingSickDay === 'approved'"
              :disabled="!!resolvingSickDay"
              @click="resolveSickDay('approved')"
            >
              <v-icon left small>mdi-check</v-icon>
              Approve
            </v-btn>
          </v-card-actions>
        </template>
      </v-card>
    </v-dialog>

    <!-- Close Request Confirmation Dialog -->
    <v-dialog v-model="closeConfirmDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">Close Timesheet Request?</v-card-title>
        <v-card-text>
          Are you sure you want to close this timesheet request? Employees will no longer be able to submit timesheets for this period.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeConfirmDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="executeCloseRequest" :loading="closingRequestId !== null">
            Close Request
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Compose Message -->
    <v-dialog v-model="messageFormDialog" max-width="560" persistent>
      <v-card>
        <v-card-title class="headline">Send a Message</v-card-title>
        <v-card-text>
          <p class="caption grey--text mb-4">
            It appears in their notification bell and pops up automatically the next time
            they open the app. They see it as coming from Payroll, not from you.
          </p>
          <v-radio-group v-model="newMessageAudience" row dense hide-details class="mt-0 mb-3">
            <v-radio label="All employees" value="all"></v-radio>
            <v-radio label="Specific departments" value="departments"></v-radio>
          </v-radio-group>
          <!-- An explicit choice above rather than "leave this empty for everyone": an
               empty box that quietly means the whole company is too easy to send by
               mistake -->
          <v-autocomplete
            v-if="newMessageAudience === 'departments'"
            v-model="newMessageDepartments"
            :items="messageDepartmentOptions"
            label="Departments"
            multiple
            small-chips
            deletable-chips
            outlined
            dense
            hide-details
            class="mb-2"
          ></v-autocomplete>
          <div class="caption grey--text mb-4" style="min-height: 20px;">
            <template v-if="messageRecipientCount !== null">
              Reaches {{ messageRecipientCount }}
              employee{{ messageRecipientCount !== 1 ? 's' : '' }} with the app.
            </template>
          </div>
          <v-text-field
            v-model="newMessageTitle"
            label="Title"
            outlined
            dense
            counter="80"
            maxlength="80"
            placeholder="e.g. Office closed Monday"
          ></v-text-field>
          <v-textarea
            v-model="newMessageBody"
            label="Message"
            outlined
            rows="6"
            counter="1000"
            maxlength="1000"
            hide-details="auto"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="sendingMessage" @click="closeMessageForm">Cancel</v-btn>
          <v-btn
            color="primary"
            :loading="sendingMessage"
            :disabled="!messageIsValid || sendingMessage"
            @click="sendPayrollMessage"
          >
            Send
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Message Confirmation -->
    <v-dialog v-model="deleteMessageConfirmDialog" max-width="420">
      <v-card>
        <v-card-title class="headline">Delete this message?</v-card-title>
        <v-card-text>
          It will disappear from every employee's notification bell.
          <div class="font-weight-medium mt-3" v-if="messageToDelete">
            {{ messageToDelete.title }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteMessageConfirmDialog = false">Cancel</v-btn>
          <v-btn color="error" :loading="deletingMessageId !== null" @click="deletePayrollMessage">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import fb from "@/main";
import axios from "axios";

// The uploaded files live in the employee app's SEPARATE Firebase project, so deleting
// one has to go through that project's Cloud Function. This is just its URL - no
// credentials or config for that project live in this repo. The endpoint verifies our
// ID token and checks our role on its side.
const EMPLOYEE_APP_DELETE_DOCUMENT_URL =
  "https://us-central1-volkindustrial-employees-app.cloudfunctions.net/adminDeleteEmployeeDocument";
import moment from "moment";

// Department codes are picked from a fixed lowercase list on the Employees page, but
// nothing enforces that on records written any other way. Messages store and compare
// this form, and the employee app's getMyNotifications normalises the same way - keep
// the two in step.
const normalizeDepartment = department => String(department || "").trim().toLowerCase();

export default {
  name: "EmployeeApp",
  data() {
    return {
      loading: true,
      timesheets: [],
      selectedRequestId: null,
      selectedRequest: null,
      requestTimesheets: [],
      selectedTimesheet: null,
      dialogOpen: false,
      reviewNotes: "",
      internalNotes: "",
      actionLoading: false,
      downloadingPdfs: false,
      // Unseen changes tracking (for comparing signed data vs current punches)
      loadingCurrentPunches: false,
      currentDailyRecords: [],
      unseenChanges: [],
      // Tab and Disputes
      activeTab: 0,
      employeeDirectory: null,
      uploads: [],
      deleteUploadDialog: false,
      uploadToDelete: null,
      deletingUpload: false,
      loadingUploads: false,
      editExpirationDialog: false,
      editExpirationMenu: false,
      uploadToEdit: null,
      editExpirationDate: null,
      editExpirationStatus: null,
      savingExpiration: false,
      expirationStatusOptions: [
        { text: "Matches the document", value: "match" },
        { text: "Flagged for review", value: "mismatch" },
        { text: "Could not be checked", value: "error" }
      ],
      uploadsLoaded: false,
      uploadsError: null,
      uploadSearch: "",
      uploadTypeFilter: "all",
      uploadCheckFilter: "all",
      uploadCheckOptions: [
        { text: "Any check result", value: "all" },
        { text: "Flagged for review", value: "mismatch" },
        { text: "Matches the document", value: "match" },
        { text: "Could not be checked", value: "error" },
        { text: "Not checked yet", value: "none" }
      ],
      uploadTypeOptions: [
        { text: "All documents", value: "all" },
        { text: "SST Card", value: "sstCard" },
        { text: "ID Document", value: "idDocument" }
      ],
      uploadHeaders: [
        { text: "Employee", value: "employeeName" },
        { text: "Badge", value: "badgeNumber" },
        { text: "Type", value: "typeLabel" },
        { text: "Uploaded", value: "uploadedAt" },
        { text: "Expires", value: "expirationDate" },
        { text: "", value: "actions", sortable: false, align: "end" }
      ],
      disputeFilter: "pending",
      disputeSearch: "",
      claimingDisputeId: null,
      userDirectory: null,
      foremanOptions: null,
      askForemanDialog: false,
      askForemanId: null,
      askForemanQuestion: "",
      askForemanScheduled: [],
      loadingAskForeman: false,
      sendingForemanAsk: false,
      withdrawingForemanAsk: false,
      punchCorrectionDialog: false,
      punchToCorrect: null,
      correctionDate: null,
      correctionTime: null,
      correctionDateMenu: false,
      savingPunchCorrection: false,
      loadingDisputes: true,
      punchDisputes: [], // Clock-out disputes from employeeAppPunchDisputes
      selectedDispute: null,
      disputeDialogOpen: false,
      disputeResolutionNotes: "",
      // Sick day requests from employeeAppSickDayRequests
      sickDayRequests: [],
      loadingSickDays: true,
      sickDaysError: null,
      sickDayFilter: "pending",
      sickDaySearch: "",
      selectedSickDay: null,
      sickDayDialogOpen: false,
      sickDayNotes: "",
      // The decision being saved ("approved" / "denied"), so only that button spins
      resolvingSickDay: null,
      sickDayHeaders: [
        { text: "Employee", value: "employeeName", sortable: true },
        { text: "Badge", value: "badgeNumber", sortable: true },
        { text: "Department", value: "department", sortable: true },
        { text: "Sick Date", value: "requestedDate", sortable: true },
        { text: "Requested", value: "requestedAt", sortable: true },
        { text: "Status", value: "status", sortable: true },
        { text: "", value: "actions", sortable: false, align: "right" }
      ],
      snackbar: {
        show: false,
        text: "",
        color: "success"
      },
      timesheetsHeaders: [
        { text: "Employee", value: "employeeName", sortable: true },
        { text: "Badge", value: "badgeNumber", sortable: true },
        { text: "Department", value: "department", sortable: true },
        { text: "Hours", value: "weeklyTotal", sortable: true, align: "right" },
        { text: "Status", value: "status", sortable: true },
        { text: "Submitted", value: "submittedAt", sortable: true },
        { text: "", value: "actions", sortable: false, align: "right" }
      ],
      disputesHeaders: [
        { text: "Employee", value: "employeeName", sortable: true},
        { text: "Badge", value: "badgeNumber", sortable: true, width: "40px" },
        // "Shift Date", not "Date": this is the day worked, which is not necessarily
        // the day the punch happened. A forgotten punch out closed the next morning
        // files under the shift, so sitting beside Punch Times a bare "Date" invites
        // reading an 8:15 AM clock out as having happened on it.
        { text: "Department", value: "department", sortable: true },
        { text: "Shift Date", value: "date", sortable: true },
        { text: "Job Site", value: "jobSiteName", sortable: true, width: "200px"},
        { text: "Punch Times", value: "punchTimes", sortable: false },
        // The chip and the foreman icon beside it are the widest things in here and
        // they are short - left unsized, the column takes space the punch times and
        // job site need more
        { text: "Status", value: "disputeStatus", sortable: true, width: "105px" },
        // Sized for "Release" plus "Review", the widest pair - "Claim" is narrower,
        // so sizing to that is what made the row jump when one was claimed
        { text: "", value: "actions", sortable: false, align: "right" }
      ],
      // Timesheet Requests
      timesheetRequests: [],
      loadingRequests: false,
      creatingRequest: false,
      closingRequestId: null,
      reopeningRequestId: null,
      refreshingCountId: null,
      // Payroll messages to employees
      newMessageTitle: "",
      newMessageBody: "",
      newMessageAudience: "all",
      newMessageDepartments: [],
      // Registered, non-terminated employees with their department and the messages
      // they have been shown. Null if any collection behind it could not be read.
      messageAudience: null,
      sendingMessage: false,
      messageFormDialog: false,
      payrollMessages: [],
      loadingMessages: false,
      deleteMessageConfirmDialog: false,
      messageToDelete: null,
      deletingMessageId: null,
      loadingRequestTimesheets: false,
      // Who worked the selected request's week but has not signed - null until loaded
      unsignedEmployees: null,
      resignEmployees: [],
      loadingUnsigned: false,
      closeConfirmDialog: false,
      closeConfirmRequest: null,
      newRequestStartDate: moment().subtract(1, 'weeks').startOf('isoWeek').format('YYYY-MM-DD'),
      newRequestEndDate: moment().subtract(1, 'weeks').endOf('isoWeek').format('YYYY-MM-DD'),
      requestStartDateMenu: false,
      requestEndDateMenu: false,
      requestsHeaders: [
        { text: "Week", value: "weekRange", sortable: true },
        { text: "Created", value: "createdAt", sortable: true },
        { text: "Created By", value: "createdByName", sortable: true },
        { text: "Submissions", value: "submissionStatus", sortable: false, align: "center" },
        { text: "Status", value: "status", sortable: true },
        { text: "", value: "actions", sortable: false, align: "right", width: "250px" }
      ]
    };
  },
  computed: {
    askForemanItems() {
      // Whoever was scheduled on that job that day first, since they were there. The
      // employee who filed the dispute is left out - a foreman cannot vouch for
      // their own time.
      const scheduled = new Set(this.askForemanScheduled);
      const employee = this.selectedDispute && this.selectedDispute.employee;
      return (this.foremanOptions || [])
        .filter(f => f.id !== employee)
        .map(f => ({ ...f, scheduled: scheduled.has(f.id) }))
        .sort((a, b) => (b.scheduled - a.scheduled) || a.name.localeCompare(b.name));
    },
    correctionIsUnchanged() {
      if (!this.punchToCorrect || !this.correctionDate || !this.correctionTime) return false;
      return `${this.correctionDate} ${this.correctionTime}` === this.punchToCorrect.recordedValue;
    },
    editDatesFound() {
      return (
        (this.uploadToEdit &&
          this.uploadToEdit.expirationCheck &&
          this.uploadToEdit.expirationCheck.datesFound) ||
        []
      );
    },
    editDatesFoundLabel() {
      return this.editDatesFound.map(d => this.formatExpiry(d)).join(" \u00b7 ");
    },
    editExpirationWillUnexpire() {
      // Warned about on the way in, because this field is what gates punching - a date
      // set in the past locks someone out and the cause is not obvious from their end
      return (
        !!this.editExpirationDate &&
        moment(this.editExpirationDate, "YYYY-MM-DD").isBefore(moment().startOf("day"))
      );
    },
    mismatchUploadCount() {
      return this.uploads.filter(
        u => u.expirationCheck && u.expirationCheck.status === "mismatch"
      ).length;
    },
    filteredUploads() {
      const search = (this.uploadSearch || "").trim().toLowerCase();
      return this.uploads.filter(upload => {
        if (this.uploadTypeFilter !== "all" && upload.type !== this.uploadTypeFilter) {
          return false;
        }
        if (this.uploadCheckFilter !== "all") {
          const status = upload.expirationCheck && upload.expirationCheck.status;
          // "none" covers uploads with no check at all - anything from before the
          // check existed, or one still mid-flight
          const matches =
            this.uploadCheckFilter === "none"
              ? !status
              : status === this.uploadCheckFilter;
          if (!matches) return false;
        }
        if (!search) return true;
        return (
          upload.employeeName.toLowerCase().includes(search) ||
          String(upload.badgeNumber).toLowerCase().includes(search)
        );
      });
    },
    requestHasPdfs() {
      return this.requestTimesheets.some(ts => ts.pdfUrl);
    },
    messageIsValid() {
      return !!(
        this.newMessageTitle.trim() &&
        this.newMessageBody.trim() &&
        (this.newMessageAudience === "all" || this.newMessageDepartments.length > 0)
      );
    },
    messageDepartmentOptions() {
      // Built from who can actually receive a message - registered, not terminated -
      // with a count, so an empty department is visible before sending to it. Falls
      // back to the employee directory, without counts, if the audience did not load.
      if (this.messageAudience) {
        const counts = {};
        this.messageAudience.forEach(member => {
          if (member.department) counts[member.department] = (counts[member.department] || 0) + 1;
        });
        return Object.keys(counts)
          .sort()
          .map(department => ({ text: `${department} (${counts[department]})`, value: department }));
      }
      const departments = new Set();
      Object.values(this.employeeDirectory || {}).forEach(employee => {
        if (!employee.terminated && employee.department) {
          departments.add(normalizeDepartment(employee.department));
        }
      });
      return [...departments].sort().map(department => ({ text: department, value: department }));
    },
    messageRecipientCount() {
      if (!this.messageAudience) return null;
      if (this.newMessageAudience === "all") return this.messageAudience.length;
      if (!this.newMessageDepartments.length) return null;
      return this.messageAudience.filter(member =>
        this.newMessageDepartments.includes(member.department)
      ).length;
    },
    pendingDisputesCount() {
      return this.punchDisputes.filter(d => !d.disputeStatus || d.disputeStatus === 'pending').length;
    },
    filteredDisputes() {
      let disputes = this.punchDisputes;
      if (this.disputeFilter === 'pending') {
        disputes = disputes.filter(d => !d.disputeStatus || d.disputeStatus === 'pending');
      } else if (this.disputeFilter === 'resolved') {
        disputes = disputes.filter(d => d.disputeStatus && d.disputeStatus !== 'pending');
      }

      const search = (this.disputeSearch || "").trim().toLowerCase();
      if (search) {
        disputes = disputes.filter(d =>
          [d.employeeName, d.badgeNumber, d.department, d.jobSiteName]
            .some(v => String(v || "").toLowerCase().includes(search))
        );
      }
      return disputes.sort((a, b) => {
        // Sort by date descending
        return moment(b.date).valueOf() - moment(a.date).valueOf();
      });
    },
    pendingSickDaysCount() {
      return this.sickDayRequests.filter(r => r.status === "pending").length;
    },
    filteredSickDays() {
      let requests = this.sickDayRequests;
      if (this.sickDayFilter === "pending") {
        requests = requests.filter(r => r.status === "pending");
      } else if (this.sickDayFilter === "resolved") {
        requests = requests.filter(r => r.status !== "pending");
      }

      const search = (this.sickDaySearch || "").trim().toLowerCase();
      if (!search) return requests;
      return requests.filter(r =>
        [r.employeeName, r.badgeNumber, r.department]
          .some(v => String(v || "").toLowerCase().includes(search))
      );
    },
    canCreateRequest() {
      return this.newRequestStartDate && this.newRequestEndDate && !this.creatingRequest;
    },
    hasUnseenChanges() {
      // True if there are changes between signed timesheet and current punch data
      return this.unseenChanges.length > 0;
    }
  },
  watch: {
    activeTab(tab) {
      // Two whole-collection reads - only pay for them if the tab is actually opened
      if (tab === 3 && !this.uploadsLoaded) {
        this.loadUploads();
      }
    },
    '$route.params.requestId': {
      immediate: true,
      async handler(requestId) {
        if (requestId) {
          this.selectedRequestId = requestId;
          await this.loadRequestTimesheets(requestId);
        } else {
          this.selectedRequestId = null;
          this.selectedRequest = null;
          this.requestTimesheets = [];
        }
      }
    }
  },
  methods: {
    async loadTimesheets() {
      this.loading = true;
      try {
        // Load timesheets (for dispute matching)
        const snapshot = await fb.db
          .collection("employeeAppTimesheetConfirmations")
          .orderBy("weekStart", "desc")
          .limit(500)
          .get();

        const directory = await this.loadEmployeeDirectory();

        // Confirmations carry the employee id and name but not the badge
        this.timesheets = snapshot.docs.map(doc => {
          const data = doc.data();
          const employee = directory[data.employee];
          return {
            id: doc.id,
            ...data,
            badgeNumber: (employee && employee.badgeNumber) || "-"
          };
        });

        // If we have a requestId in route, load those timesheets
        if (this.$route.params.requestId) {
          this.selectedRequestId = this.$route.params.requestId;
          await this.loadRequestTimesheets(this.selectedRequestId);
        }
      } catch (error) {
        console.error("Error loading timesheets:", error);
        this.showSnackbar("Error loading timesheets", "error");
      } finally {
        this.loading = false;
      }
    },
    async loadRequestTimesheets(requestId) {
      this.loadingRequestTimesheets = true;
      this.unsignedEmployees = null;
      this.resignEmployees = [];
      try {
        // Load the request details
        const requestDoc = await fb.db
          .collection("employeeAppTimesheetRequests")
          .doc(requestId)
          .get();

        if (requestDoc.exists) {
          this.selectedRequest = { id: requestDoc.id, ...requestDoc.data() };
        } else {
          this.selectedRequest = null;
          this.requestTimesheets = [];
          return;
        }

        // Load timesheets for this request
        const snapshot = await fb.db
          .collection("employeeAppTimesheetConfirmations")
          .where("timesheetRequestId", "==", requestId)
          .get();

        const directory = await this.loadEmployeeDirectory();

        this.requestTimesheets = snapshot.docs
          .map(doc => {
            const data = doc.data();
            const employee = directory[data.employee];
            return {
              id: doc.id,
              ...data,
              badgeNumber: (employee && employee.badgeNumber) || "-"
            };
          })
          .sort((a, b) => (a.employeeName || '').localeCompare(b.employeeName || ''));
      } catch (error) {
        console.error("Error loading request timesheets:", error);
        this.showSnackbar("Error loading timesheets", "error");
      } finally {
        this.loadingRequestTimesheets = false;
      }
    },
    viewTimesheetRequest(request) {
      this.$router.push(`/employee-app/${request.id}`);
    },
    async loadPunchDisputes() {
      this.loadingDisputes = true;
      try {
        const snapshot = await fb.db
          .collection("employeeAppPunchDisputes")
          .orderBy("createdAt", "desc")
          .limit(200)
          .get();

        const [directory] = await Promise.all([
          this.loadEmployeeDirectory(),
          this.loadUserDirectory(),
        ]);

        const disputes = snapshot.docs.map(doc => {
          const data = doc.data();
          const employee = directory[data.employee];
          // Which punch id is present is what says what this dispute is about. Both
          // means a record written before disputes became per-punch, when one dispute
          // covered an in/out pair.
          const isLegacyPair = !!data.punchInId && !!data.punchOutId;

          return {
            id: doc.id,
            ...data,
            isLegacyPair,
            disputeDirection: isLegacyPair ? null : (data.punchInId ? "in" : "out"),
            // Dispute records carry the employee id and name but not the badge or
            // department, so both come off the employee directory
            badgeNumber: (employee && employee.badgeNumber) || "-",
            department: (employee && employee.department) || "",
            // Declared up front, every one of them, because Vue 2 only tracks
            // properties that exist when it observes the object. An unclaimed dispute
            // has no claimedBy key and a never-edited punch has no editedBy, so
            // assigning either after a claim or a correction added a property Vue
            // could not see - the button kept its old label until a reload.
            // foremanReview is the same case: absent until a foreman is asked.
            claimedBy: data.claimedBy || null,
            foremanReview: data.foremanReview || null,
            punchInExists: false,
            punchOutExists: false,
            punchInTimeRaw: null,
            punchOutTimeRaw: null,
            punchInRecordDirection: null,
            punchOutRecordDirection: null,
            punchInEditedBy: null,
            punchOutEditedBy: null,
            actualPunchInTime: null,
            actualPunchOutTime: null,
            actualPunchInDate: null,
            actualPunchOutDate: null,
            lateClockOutReason: null
          };
        });

        // Punch records for every dispute, fetched in bulk.
        //
        // This used to walk the disputes one at a time, awaiting one or two document
        // reads each - up to 400 serial round trips for a full page of 200, which is
        // why the tab sat empty for so long. Ten ids per query, all queries in
        // parallel, turns that into roughly forty concurrent reads.
        const punchIds = [];
        disputes.forEach(dispute => {
          if (dispute.punchOutId) punchIds.push(dispute.punchOutId);
          if (dispute.punchInId) punchIds.push(dispute.punchInId);
        });

        const punchesById = {};
        try {
          const snapshots = await Promise.all(
            this.chunkForInQuery([...new Set(punchIds)]).map(chunk =>
              fb.db
                .collection("punchRecords")
                .where(fb.firebase.firestore.FieldPath.documentId(), "in", chunk)
                .get()
            )
          );
          snapshots.forEach(snap => snap.forEach(doc => { punchesById[doc.id] = doc.data(); }));
        } catch (err) {
          console.warn("Error fetching punch records for disputes:", err);
        }

        // There used to be a fallback here that guessed a dispute's IN punch from
        // employee + job site + date, for records written before punchInId was stored.
        // It has been removed: a modern out dispute has no punchInId either, and the
        // two are indistinguishable - so the fallback would have invented an in punch
        // for every out dispute. Those few old records now show only their out punch,
        // which is accurate rather than guessed.
        disputes.forEach(dispute => {
          // Not `if (!dispute.punchOutId) return` - that dropped every clock-in
          // dispute silently, since those have no out punch by design
          if (dispute.punchOutId) {
            const outPunch = punchesById[dispute.punchOutId];
            // Whether the record is still there decides if it can be corrected -
            // payroll deletes and re-adds punches, which orphans the dispute's id
            dispute.punchOutExists = !!outPunch;
            if (outPunch && outPunch.time) {
              dispute.actualPunchOutTime = this.formatPunchTime(outPunch.time);
              dispute.actualPunchOutDate = this.formatPunchDate(outPunch.time);
              // Raw, for prefilling the editor - the formatted pair above cannot be
              // parsed back reliably (no year)
              dispute.punchOutTimeRaw = outPunch.time;
            }
            // The record's own direction, not inferred from which id is set. Payroll
            // can flip a punch's direction in bulk, and then the two disagree - the
            // record is what payroll is actually working with.
            if (outPunch && outPunch.direction) {
              dispute.punchOutRecordDirection = outPunch.direction;
            }
            // Set by whoever last changed the punch, here or in Payroll. Only its
            // presence is used - resolving the uid to a name would mean loading the
            // users collection into this view, and Payroll already shows the name.
            if (outPunch && outPunch.editedBy) {
              dispute.punchOutEditedBy = outPunch.editedBy;
            }
            // A clock out past the scheduled end needs an overtime reason, which is
            // stored on the punch - a SEPARATE explanation from the dispute reason.
            // Both can exist on one punch: "why I stayed late" and "this time is
            // wrong" are different claims, and the dialog showed only the latter.
            // Forgotten punch outs null this field, so there is no double display.
            if (outPunch && outPunch.lateClockOutReason) {
              dispute.lateClockOutReason = outPunch.lateClockOutReason;
            }
          }

          if (dispute.punchInId) {
            const inPunch = punchesById[dispute.punchInId];
            dispute.punchInExists = !!inPunch;
            if (inPunch && inPunch.time) {
              dispute.actualPunchInTime = this.formatPunchTime(inPunch.time);
              dispute.actualPunchInDate = this.formatPunchDate(inPunch.time);
              dispute.punchInTimeRaw = inPunch.time;
            }
            if (inPunch && inPunch.direction) {
              dispute.punchInRecordDirection = inPunch.direction;
            }
            if (inPunch && inPunch.editedBy) {
              dispute.punchInEditedBy = inPunch.editedBy;
            }
          }
        });

        this.punchDisputes = disputes;

        // The pending-count chip widens the Disputes tab once it loads, but the tab
        // slider was already positioned - without this it sits misaligned under the
        // tab to the right
        this.$nextTick(() => {
          if (this.$refs.tabs) this.$refs.tabs.callSlider();
        });
      } catch (error) {
        console.error("Error loading punch disputes:", error);
        this.showSnackbar("Error loading disputes", "error");
      } finally {
        this.loadingDisputes = false;
      }
    },
    formatPunchTime(timestamp) {
      // Convert Firestore timestamp to readable time string
      let date;
      if (timestamp.toDate) {
        date = timestamp.toDate();
      } else if (timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else {
        date = new Date(timestamp);
      }
      return moment(date).format("h:mm A");
    },
    formatShiftDate(dateString) {
      // Carries the year, unlike the shared formatDate - a dispute can be reviewed
      // in a different year to the one it was filed in
      return dateString
        ? moment(dateString, "YYYY-MM-DD").format("ddd, MMM D, YYYY")
        : "-";
    },
    punchRecordDirection(dispute) {
      // Taken off the punch records themselves. Null means none of the ids this
      // dispute points at resolved to a record - payroll deletes and re-adds punches
      // when correcting them, which leaves the dispute pointing at nothing.
      if (!dispute) return null;
      const label = d => (d === "in" ? "In" : d === "out" ? "Out" : d);
      const parts = [];
      if (dispute.punchInId && dispute.punchInRecordDirection) {
        parts.push(label(dispute.punchInRecordDirection));
      }
      if (dispute.punchOutId && dispute.punchOutRecordDirection) {
        parts.push(label(dispute.punchOutRecordDirection));
      }
      return parts.length ? parts.join(" · ") : null;
    },
    punchRecordEdited(dispute) {
      if (!dispute) return false;
      return dispute.isLegacyPair
        ? !!(dispute.punchOutEditedBy || dispute.punchInEditedBy)
        : dispute.disputeDirection === "in"
        ? !!dispute.punchInEditedBy
        : !!dispute.punchOutEditedBy;
    },
    punchRecordTime(dispute) {
      // The live record as read when the tab loaded - not the dispute's own snapshot.
      // Null means payroll has deleted the punch since the dispute was filed, and the
      // field says so in red rather than showing a dash.
      if (!dispute) return null;
      const raw = dispute.isLegacyPair
        ? dispute.punchOutTimeRaw || dispute.punchInTimeRaw
        : dispute.disputeDirection === "in"
        ? dispute.punchInTimeRaw
        : dispute.punchOutTimeRaw;
      if (!raw) return null;
      const d = raw.toDate ? raw.toDate() : raw.seconds ? new Date(raw.seconds * 1000) : new Date(raw);
      return moment(d).format("ddd, MMM D, YYYY h:mm A");
    },
    async loadUserDirectory() {
      // Admin names by uid, cached for the life of the page. Only claimedBy is stored
      // on a dispute, so the name is resolved here rather than written alongside it -
      // a stored copy would be a second version of someone's name, stale the moment
      // it changed in users.
      if (this.userDirectory) return this.userDirectory;

      const directory = {};
      try {
        const snapshot = await fb.db.collection("users").get();
        snapshot.forEach(doc => {
          const data = doc.data();
          const name = `${data.firstName || ""} ${data.lastName || ""}`.trim() || data.email || null;
          // Keyed by both, since a users document is not guaranteed to be keyed by uid
          if (name) {
            directory[doc.id] = name;
            if (data.uid) directory[data.uid] = name;
          }
        });
      } catch (error) {
        // A claim still works without a name - the button greys out either way
        console.warn("Could not load user names for dispute claims:", error);
      }

      this.userDirectory = directory;
      return directory;
    },
    canActionDispute(dispute) {
      // Claiming is a required step, not a hint: nothing can be changed on a dispute
      // until the person doing it has put their name on it. Claim from the list.
      return this.isDisputePending(dispute) && this.claimedByMe(dispute);
    },
    actionBlockedReason(dispute) {
      if (!this.isDisputePending(dispute)) return null;
      if (this.claimedByOther(dispute)) {
        return `${this.claimTooltip(dispute)}. Only they can action it, and only they can release it.`;
      }
      // Nothing for the unclaimed case: the action bar says "Claim to action", which
      // is the whole message and sits next to the buttons it explains
      return null;
    },
    needsClaim(dispute) {
      return (
        this.isDisputePending(dispute) &&
        !this.claimedByMe(dispute) &&
        !this.claimedByOther(dispute)
      );
    },
    claimedByMe(dispute) {
      return !!dispute && dispute.claimedBy === this.$store.state.user.uid;
    },
    claimedByOther(dispute) {
      return !!dispute && !!dispute.claimedBy && dispute.claimedBy !== this.$store.state.user.uid;
    },
    claimedByName(dispute) {
      return (
        (this.userDirectory && dispute && this.userDirectory[dispute.claimedBy]) ||
        "another reviewer"
      );
    },
    claimTooltip(dispute) {
      if (this.claimedByOther(dispute)) return `Claimed by ${this.claimedByName(dispute)}`;
      if (this.claimedByMe(dispute)) return "You claimed this - release it to hand it back";
      return "Claim this so the others know you are on it";
    },
    async claimDispute(dispute) {
      this.claimingDisputeId = dispute.id;
      try {
        // A transaction, not a plain update: two people pressing Claim at the same
        // moment would otherwise both succeed and the second would quietly take it.
        const ref = fb.db.collection("employeeAppPunchDisputes").doc(dispute.id);
        const me = this.$store.state.user;
        const heldBy = await fb.db.runTransaction(async tx => {
          const doc = await tx.get(ref);
          const held = doc.exists ? doc.data().claimedBy : null;
          if (held && held !== me.uid) return held;
          tx.update(ref, { claimedBy: me.uid });
          return null;
        });

        if (heldBy) {
          // Reflect what is actually there rather than leaving a stale Claim button
          await this.loadPunchDisputes();
          const who = (this.userDirectory && this.userDirectory[heldBy]) || "another reviewer";
          this.showSnackbar(`${who} claimed this first`, "warning");
          return;
        }

        dispute.claimedBy = me.uid;
      } catch (error) {
        console.error("Error claiming dispute:", error);
        this.showSnackbar("Could not claim this dispute", "error");
      } finally {
        this.claimingDisputeId = null;
      }
    },
    async releaseDispute(dispute) {
      this.claimingDisputeId = dispute.id;
      try {
        await fb.db.collection("employeeAppPunchDisputes").doc(dispute.id).update({
          claimedBy: fb.firebase.firestore.FieldValue.delete(),
        });
        dispute.claimedBy = null;
      } catch (error) {
        console.error("Error releasing dispute:", error);
        this.showSnackbar("Could not release this dispute", "error");
      } finally {
        this.claimingDisputeId = null;
      }
    },
    isDisputePending(dispute) {
      return !!dispute && (!dispute.disputeStatus || dispute.disputeStatus === "pending");
    },
    foremanName(dispute) {
      // An employee id, so the name comes off the employee directory, not users
      const id = dispute && dispute.foremanReview && dispute.foremanReview.foremanId;
      const employee = this.employeeDirectory && this.employeeDirectory[id];
      return (employee && employee.name) || "Foreman";
    },
    foremanReviewLabel(dispute) {
      const review = dispute.foremanReview;
      if (!review.respondedAt) return "Awaiting answer";
      return review.verdict === "agree" ? "Agrees with employee" : "Disagrees with employee";
    },
    foremanReviewColor(dispute) {
      const review = dispute.foremanReview;
      if (!review.respondedAt) return "grey";
      return review.verdict === "agree" ? "success" : "orange";
    },
    foremanReviewTooltip(dispute) {
      return `${this.foremanName(dispute)}: ${this.foremanReviewLabel(dispute).toLowerCase()}`;
    },
    canWithdrawForemanAsk(dispute) {
      return this.canActionDispute(dispute) && !!dispute.foremanReview && !dispute.foremanReview.respondedAt;
    },
    async loadForemanOptions() {
      // Foremen and supervisors by EMPLOYEE id. They answer in the employee app, which
      // knows people as employees - an admin login is not linked to one - so this is
      // the id that app can match against whoever is signed in.
      //
      // Only those registered in the employee app: anyone else could be sent a
      // question with no way of ever seeing it. Cached for the life of the page.
      if (this.foremanOptions) return this.foremanOptions;

      const directory = await this.loadEmployeeDirectory();
      const candidates = Object.keys(directory).filter(id => {
        const e = directory[id];
        return !e.terminated && ["foreman", "supervisor"].includes(e.department);
      });

      const registered = new Set();
      const snapshots = await Promise.all(
        this.chunkForInQuery(candidates).map(chunk =>
          fb.db
            .collection("employeeApp")
            .where(fb.firebase.firestore.FieldPath.documentId(), "in", chunk)
            .get()
        )
      );
      snapshots.forEach(snap => snap.forEach(doc => {
        if (doc.data().firebaseAuthUid) registered.add(doc.id);
      }));

      this.foremanOptions = candidates
        .filter(id => registered.has(id))
        .map(id => ({ id, name: directory[id].name, department: directory[id].department }));
      return this.foremanOptions;
    },
    async openAskForeman(dispute) {
      this.askForemanId = null;
      this.askForemanQuestion = "";
      this.askForemanScheduled = [];
      this.askForemanDialog = true;

      this.loadingAskForeman = true;
      try {
        const [options, schedules] = await Promise.all([
          this.loadForemanOptions(),
          fb.db.collection("employeeSchedule").where("jobSite", "==", dispute.jobSiteId).get()
        ]);

        // Who was scheduled on that job on the shift date - the same weekday and date
        // range test the employee app uses when it records shiftStartTime
        const shiftDate = moment(dispute.date, "YYYY-MM-DD");
        const weekday = shiftDate.format("dddd");
        const scheduled = new Set();
        schedules.forEach(doc => {
          const s = doc.data();
          const inRange =
            (!s.firstShiftDate || s.firstShiftDate <= dispute.date) &&
            (!s.lastShiftDate || s.lastShiftDate >= dispute.date);
          if (inRange && (s.daysOfWeek || []).includes(weekday)) {
            (s.employees || []).forEach(id => scheduled.add(id));
          }
        });

        const onShift = options.filter(f => scheduled.has(f.id) && f.id !== dispute.employee);
        this.askForemanScheduled = onShift.map(f => f.id);
        // Preselected only when exactly one foreman was on that job that day, so two
        // makes payroll choose rather than quietly asking the first
        if (onShift.length === 1) this.askForemanId = onShift[0].id;
      } catch (error) {
        console.warn("Could not load foremen for this dispute:", error);
      } finally {
        this.loadingAskForeman = false;
      }
    },
    async sendForemanAsk() {
      const dispute = this.selectedDispute;
      if (!dispute || !this.askForemanId || !this.askForemanQuestion.trim()) return;

      this.sendingForemanAsk = true;
      try {
        // A transaction for the same reason as claiming: the dialog's view of the
        // dispute may be stale, and this must not overwrite a request someone else
        // sent, or one the foreman has already answered.
        const ref = fb.db.collection("employeeAppPunchDisputes").doc(dispute.id);
        const me = this.$store.state.user.uid;
        const review = {
          // An employee id - the foreman answers in the employee app, signed in as
          // themselves, and its answerForemanDispute endpoint matches on this
          foremanId: this.askForemanId,
          question: this.askForemanQuestion.trim(),
          requestedBy: me,
          requestedAt: fb.firebase.firestore.FieldValue.serverTimestamp(),
          // Written as null rather than left out, so every reader sees the same shape
          // whether or not the foreman has answered yet
          verdict: null,
          notes: null,
          respondedAt: null
        };
        const blocked = await fb.db.runTransaction(async tx => {
          const doc = await tx.get(ref);
          const data = doc.exists ? doc.data() : {};
          if (data.claimedBy !== me) return "This dispute is no longer claimed by you";
          if (data.disputeStatus && data.disputeStatus !== "pending") return "This dispute has been resolved";
          if (data.foremanReview) return "A foreman has already been asked about this dispute";
          tx.update(ref, { foremanReview: review });
          return null;
        });

        if (blocked) {
          this.askForemanDialog = false;
          this.disputeDialogOpen = false;
          this.showSnackbar(blocked, "warning");
          await this.loadPunchDisputes();
          return;
        }

        dispute.foremanReview = { ...review, requestedAt: new Date() };
        this.askForemanDialog = false;
        this.showSnackbar(`Sent to ${this.foremanName(dispute)}`, "success");
      } catch (error) {
        console.error("Error asking the foreman:", error);
        this.showSnackbar("Could not send this to the foreman", "error");
      } finally {
        this.sendingForemanAsk = false;
      }
    },
    async withdrawForemanAsk() {
      const dispute = this.selectedDispute;
      if (!this.canWithdrawForemanAsk(dispute)) return;

      this.withdrawingForemanAsk = true;
      try {
        // Checked inside the transaction: the foreman may have answered since this
        // dialog opened, and deleting the request would throw their answer away
        const ref = fb.db.collection("employeeAppPunchDisputes").doc(dispute.id);
        const answered = await fb.db.runTransaction(async tx => {
          const doc = await tx.get(ref);
          const review = doc.exists ? doc.data().foremanReview : null;
          if (review && review.respondedAt) return true;
          tx.update(ref, { foremanReview: fb.firebase.firestore.FieldValue.delete() });
          return false;
        });

        if (answered) {
          this.disputeDialogOpen = false;
          this.showSnackbar("The foreman has already answered - reopen the dispute to see it", "warning");
          await this.loadPunchDisputes();
          return;
        }

        dispute.foremanReview = null;
      } catch (error) {
        console.error("Error withdrawing the foreman request:", error);
        this.showSnackbar("Could not withdraw the request", "error");
      } finally {
        this.withdrawingForemanAsk = false;
      }
    },
    punchRecordExists(dispute, direction) {
      if (!dispute) return false;
      return direction === "in" ? !!dispute.punchInExists : !!dispute.punchOutExists;
    },
    canCorrectPunch(dispute, direction) {
      // Only while open, and only when there is a record to change. A resolved
      // dispute has been acted on already; editing the punch underneath it would
      // leave the resolution notes describing a time that is no longer there.
      //
      // And only by whoever claimed it. A claim is there to stop two people working
      // the same dispute, which is worth nothing if anyone else can still move the
      // punch under them mid-review.
      if (!direction) return false;
      return this.canActionDispute(dispute) && this.punchRecordExists(dispute, direction);
    },
    openPunchCorrection(dispute, direction) {
      const raw = direction === "in" ? dispute.punchInTimeRaw : dispute.punchOutTimeRaw;
      const toDate = v => (v && v.toDate ? v.toDate() : v && v.seconds ? new Date(v.seconds * 1000) : v ? new Date(v) : null);
      const recorded = toDate(raw);
      const claimed = toDate(dispute.claimedPunchAt);

      this.punchToCorrect = {
        disputeId: dispute.id,
        punchId: direction === "in" ? dispute.punchInId : dispute.punchOutId,
        direction,
        employeeName: dispute.employeeName,
        // Only what this dialog needs to tell "already that value" from a change. The
        // shift, the claim and the filed punch are all on the dispute dialog behind it.
        recordedValue: recorded ? moment(recorded).format("YYYY-MM-DD HH:mm") : null,
      };

      // Prefilled with what the employee says it should have been, since applying
      // their claim is the usual reason for opening this. Falls back to the recorded
      // time so the fields are never empty.
      const seed = claimed || recorded;
      this.correctionDate = seed ? moment(seed).format("YYYY-MM-DD") : null;
      this.correctionTime = seed ? moment(seed).format("HH:mm") : null;
      this.punchCorrectionDialog = true;
    },
    async savePunchCorrection() {
      if (!this.punchToCorrect || this.correctionIsUnchanged) return;

      const when = moment(`${this.correctionDate} ${this.correctionTime}`, "YYYY-MM-DD HH:mm", true);
      if (!when.isValid()) { this.showSnackbar("That date and time could not be read", "error"); return; }

      this.savingPunchCorrection = true;
      try {
        // punchRecords lives in this project's Firestore, so this writes directly -
        // the same way Payroll does when it changes a punch time.
        //
        // editedBy is the convention punch records already carry: Payroll writes the
        // editor's uid here and shows it as "Edited by ..." against the punch. Using
        // the same field means a correction made here is visible there too, and
        // nothing about it needs recording on the dispute.
        //
        // roundedTimeShown is deliberately left alone: it holds the time as the
        // employee acknowledged it, and comparing the two is the only way to tell
        // afterwards that a punch was moved at all.
        await fb.db.collection("punchRecords").doc(this.punchToCorrect.punchId).update({
          time: when.toDate(),
          editedBy: this.$store.state.user.uid,
        });

        // Patch the open dialog so it reflects the change without a full reload
        const d = this.selectedDispute;
        if (d && d.id === this.punchToCorrect.disputeId) {
          const label = this.formatPunchTime(when.toDate());
          const dateLabel = this.formatPunchDate(when.toDate());
          // The as-filed snapshot is left alone on purpose. It is the record of what
          // the employee's app actually sent, and a correction does not change that -
          // the new value shows as its own line instead.
          if (this.punchToCorrect.direction === "in") {
            d.punchInEditedBy = this.$store.state.user.uid;
            d.punchInTimeRaw = when.toDate();
            d.actualPunchInTime = label;
            d.actualPunchInDate = dateLabel;
          } else {
            d.punchOutEditedBy = this.$store.state.user.uid;
            d.punchOutTimeRaw = when.toDate();
            d.actualPunchOutTime = label;
            d.actualPunchOutDate = dateLabel;
          }
        }

        this.punchCorrectionDialog = false;
        this.punchToCorrect = null;
        this.showSnackbar("Punch time updated", "success");
      } catch (error) {
        console.error("Error correcting the punch time:", error);
        this.showSnackbar("Could not update the punch time", "error");
      } finally {
        this.savingPunchCorrection = false;
      }
    },
    shiftWindow(dispute) {
      const start = dispute && dispute.shiftStartTime;
      const end = dispute && dispute.shiftEndTime;
      if (!start && !end) return null;

      const at = t => (t ? moment(t, "HH:mm").format("h:mm A") : "--");
      // An end earlier than the start is a shift that runs past midnight, which is
      // common here and the reason a bare "7:00 PM - 3:00 AM" needs saying out loud
      const overnight = start && end && end < start;
      return `${at(start)} - ${at(end)}${overnight ? " (next day)" : ""}`;
    },
    pairPunchTime(dispute, direction) {
      // Same preference order as the single-punch branch: the dispute's own snapshot
      // first, then the live punch record, then the rounded string. Without this the
      // snapshot never showed on pair records, and their punch records are the ones
      // most likely to have been deleted.
      if (!dispute) return "--";
      const at = direction === "in" ? dispute.punchInAt : dispute.punchOutAt;
      if (at) return this.formatPunchTime(at);
      return direction === "in"
        ? dispute.actualPunchInTime || dispute.punchInTime || "--"
        : dispute.actualPunchOutTime || dispute.punchOutTime || "--";
    },
    disputedPunchTime(dispute) {
      // The time of the one punch this dispute is about
      if (!dispute) return "--";
      const filedAt = dispute.disputeDirection === "in" ? dispute.punchInAt : dispute.punchOutAt;
      if (filedAt) return this.formatPunchTime(filedAt);
      return dispute.disputeDirection === "in"
        ? dispute.actualPunchInTime || dispute.punchInTime || "--"
        : dispute.actualPunchOutTime || dispute.punchOutTime || "--";
    },
    disputedPunchDate(dispute) {
      if (!dispute) return null;
      const filedAt = dispute.disputeDirection === "in" ? dispute.punchInAt : dispute.punchOutAt;
      if (filedAt) return this.formatPunchDate(filedAt);
      return dispute.disputeDirection === "in"
        ? this.disputePunchDate(dispute, "in")
        : dispute.actualPunchOutDate || null;
    },
    disputePunchDate(dispute, direction) {
      if (!dispute) return null;
      if (direction === "in") {
        // The dispute's own date IS the in punch's Eastern date, so fall back to it
        // when the punch record could not be read
        return (
          dispute.actualPunchInDate ||
          (dispute.date ? moment(dispute.date, "YYYY-MM-DD").format("M/D") : null)
        );
      }
      // Never fall back for the out punch. A forgotten punch out is closed the NEXT
      // day, so defaulting it to the dispute date would assert the opposite of what
      // happened - better to show no date than a wrong one.
      return dispute.actualPunchOutDate || null;
    },
    isCrossDayDispute(dispute) {
      // True when the IN and OUT punches fall on different days - e.g. a forgotten
      // punch out closed the next morning.
      //
      // Compares the same dates the row displays. Reading actualPunchInDate directly
      // meant a dispute whose in punch record could not be read showed its dates
      // (which fall back to the dispute's own date) but was never flagged cross-day -
      // so two rows spanning the same two days were coloured differently.
      // Only meaningful for a pair - a single-punch dispute spans nothing
      if (!dispute || !dispute.isLegacyPair) return false;
      const inDate = this.disputePunchDate(dispute, "in");
      const outDate = this.disputePunchDate(dispute, "out");
      if (!inDate || !outDate) return false;
      return inDate !== outDate;
    },
    formatPunchDate(timestamp) {
      // Short date for a punch, e.g. "9/8"
      let date;
      if (timestamp.toDate) {
        date = timestamp.toDate();
      } else if (timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else {
        date = new Date(timestamp);
      }
      return moment(date).format("M/D");
    },
    goBack() {
      this.selectedRequestId = null;
      this.selectedRequest = null;
      this.requestTimesheets = [];
      this.$router.push('/employee-app');
    },
    async openTimesheetDialog(timesheet) {
      this.selectedTimesheet = timesheet;
      this.reviewNotes = "";
      this.internalNotes = "";
      this.unseenChanges = [];
      this.currentDailyRecords = [];
      this.dialogOpen = true;

      // Fetch fresh dispute data for this timesheet's OUT punches
      if (timesheet.hasDisputes && timesheet.dailyRecords) {
        await this.refreshTimesheetDisputes(timesheet);
      }

      // Overtime reasons are unconditional - an OT punch under the forgotten-punch-out
      // threshold creates no dispute, so hasDisputes is false and the call above is skipped
      if (timesheet.dailyRecords) {
        await this.loadOvertimeReasons(timesheet);
      }

      // For pending timesheets, check if punch data has changed since employee signed
      if (timesheet.status !== 'approved' && timesheet.dailyRecords) {
        await this.checkForUnseenChanges(timesheet);
      }
    },
    async checkForUnseenChanges(timesheet) {
      // Compare signed timesheet data with current punch records by punch ID
      // This is more reliable than comparing rounded times
      this.loadingCurrentPunches = true;
      try {
        // Collect all punch IDs from the signed timesheet
        const signedPunchIds = [];
        const signedPunchMap = {}; // Map of punchId -> { direction, actualTime, date, lunches }
        // Dispute statuses as signed, per day and direction. The signed disputes carry
        // no punch id, only which side of the shift they were about.
        const signedDisputes = {}; // `${date}|${direction}` -> sorted statuses

        (timesheet.dailyRecords || []).forEach(record => {
          // The raw punches as they were when the employee signed - the meals and
          // no-meal claims live on these, not on the timeline
          const signedPunches = {};
          (record.punches || []).forEach(p => { if (p.id) signedPunches[p.id] = p; });

          (record.punchTimeline || []).forEach(punch => {
            if (punch.id) {
              signedPunchIds.push(punch.id);
              signedPunchMap[punch.id] = {
                direction: punch.direction,
                actualTime: punch.actualTime, // The actual time when employee signed
                date: record.date,
                lunches: signedPunches[punch.id] ? this.lunchSummary(signedPunches[punch.id]) : null
              };
            }
          });

          (record.disputes || []).forEach(d => {
            const key = `${record.date}|${d.direction || 'out'}`;
            (signedDisputes[key] = signedDisputes[key] || []).push(this.normalizeDisputeStatus(d.status));
          });
        });

        if (signedPunchIds.length === 0) {
          this.loadingCurrentPunches = false;
          return;
        }

        // Fetch current punch records by their IDs, ten at a time - see chunkForInQuery
        const currentPunchMap = {}; // Map of punchId -> { direction, actualTime }

        for (const chunk of this.chunkForInQuery(signedPunchIds)) {
          const snapshot = await fb.db
            .collection("punchRecords")
            .where(fb.firebase.firestore.FieldPath.documentId(), "in", chunk)
            .get();

          snapshot.forEach(doc => {
            const punch = doc.data();
            // Eastern, like the signed actualTime - in the browser's zone this reported
            // every punch as changed for anyone outside New York
            const actualTime = this.formatEasternTime(punch.time);
            currentPunchMap[doc.id] = {
              direction: punch.direction,
              actualTime: actualTime,
              lunches: this.lunchSummary(punch)
            };
          });
        }

        // Current dispute statuses for the signed punches. A decision made after
        // signing changes the sheet - a denied claim is no longer paid as entered, so
        // the hours move without any punch moving.
        const currentDisputes = {}; // `${date}|${direction}` -> sorted statuses
        const seenDisputes = new Set();
        for (const chunk of this.chunkForInQuery(signedPunchIds)) {
          const snapshots = await Promise.all([
            fb.db.collection("employeeAppPunchDisputes").where("punchOutId", "in", chunk).get(),
            fb.db.collection("employeeAppPunchDisputes").where("punchInId", "in", chunk).get()
          ]);
          snapshots.forEach(snapshot => snapshot.forEach(doc => {
            if (seenDisputes.has(doc.id)) return;
            seenDisputes.add(doc.id);
            const d = doc.data();
            const signed = signedPunchMap[d.punchOutId] || signedPunchMap[d.punchInId];
            if (!signed) return;
            const direction = d.punchInId && !d.punchOutId ? 'in' : 'out';
            const key = `${signed.date}|${direction}`;
            (currentDisputes[key] = currentDisputes[key] || []).push(this.normalizeDisputeStatus(d.disputeStatus));
          }));
        }

        // Also check for new punches added in this week that weren't in the signed timesheet
        const weekStart = moment(timesheet.weekStart, "YYYY-MM-DD").startOf("day");
        const weekEnd = moment(timesheet.weekEnd, "YYYY-MM-DD").endOf("day");

        const allPunchesSnapshot = await fb.db
          .collection("punchRecords")
          .where("employee", "==", timesheet.employee)
          .where("time", ">=", weekStart.toDate())
          .where("time", "<=", weekEnd.toDate())
          .get();

        const newPunches = []; // Punches that exist now but weren't in signed timesheet
        allPunchesSnapshot.forEach(doc => {
          if (!signedPunchMap[doc.id]) {
            const punch = doc.data();
            newPunches.push({
              id: doc.id,
              direction: punch.direction,
              actualTime: this.formatEasternTime(punch.time),
              date: moment(punch.time.toDate()).format("YYYY-MM-DD")
            });
          }
        });

        // Compare signed punches with current punches
        this.compareTimesheetDataByPunchId(signedPunchMap, currentPunchMap, newPunches, signedDisputes, currentDisputes);
      } catch (error) {
        console.error("Error checking for unseen changes:", error);
      } finally {
        this.loadingCurrentPunches = false;
      }
    },
    formatPunchTime(timestamp) {
      // Format timestamp to time string (same format used in dailyRecords)
      let date;
      if (timestamp.toDate) {
        date = timestamp.toDate();
      } else if (timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else {
        date = new Date(timestamp);
      }
      return moment(date).format("h:mm A");
    },
    formatEasternTime(timestamp) {
      // "h:mm A" in Eastern Time, the format and zone of the signed actualTime. Intl
      // rather than moment-timezone, which this app does not ship. Newer browsers put a
      // narrow no-break space before AM/PM - normalised so the strings compare.
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds ? timestamp.seconds * 1000 : timestamp);
      return date
        .toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" })
        .replace(/[\u202f\u00a0]/g, " ");
    },
    lunchSummary(punch) {
      // Meals and no-meal claims on a clock out, reduced to what changes the sheet: a
      // meal's time, or a claim's status and the meal time payroll set when denying it
      if (punch.direction !== 'out') return null;
      const lunches = Array.isArray(punch.lunches) ? punch.lunches : Object.values(punch.lunches || {});
      return lunches
        .filter(Boolean)
        .map(l => (l.time ? `Meal at ${l.time}` : l.notes ? `No meal claim ${l.approvalStatus || 'pending'}${l.overrideTime ? ` (meal at ${l.overrideTime})` : ''}` : ''))
        .filter(Boolean)
        .join(', ');
    },
    normalizeDisputeStatus(status) {
      // "Not Approved" writes closed; older records hold denied or resolved
      if (!status || status === 'pending') return 'pending';
      return status === 'approved' ? 'approved' : 'denied';
    },
    compareTimesheetDataByPunchId(signedPunchMap, currentPunchMap, newPunches, signedDisputes = {}, currentDisputes = {}) {
      // Compare signed punch data with current punch data by punch ID
      // This compares actual times (not rounded) to detect real changes
      const changes = [];

      // Check each signed punch against current data
      for (const [punchId, signed] of Object.entries(signedPunchMap)) {
        const current = currentPunchMap[punchId];

        if (!current) {
          // Punch was deleted
          changes.push({
            type: 'removed',
            dayLabel: this.formatDayOfWeek(signed.date) + ', ' + this.formatDateShort(signed.date),
            description: `Punch ${signed.direction.toUpperCase()} removed`,
            oldValue: signed.actualTime || '—',
            newValue: '—'
          });
          continue;
        }

        // Compare actual times
        if (signed.actualTime !== current.actualTime) {
          changes.push({
            type: 'punch',
            dayLabel: this.formatDayOfWeek(signed.date) + ', ' + this.formatDateShort(signed.date),
            description: `Punch ${signed.direction.toUpperCase()} time changed`,
            oldValue: signed.actualTime || '—',
            newValue: current.actualTime || '—'
          });
        }

        // Meals and no-meal decisions. Only for weeks whose signed record kept the raw
        // punches - without them there is nothing to compare against.
        if (signed.lunches !== null && signed.lunches !== undefined && signed.lunches !== current.lunches) {
          changes.push({
            type: 'lunch',
            dayLabel: this.formatDayOfWeek(signed.date) + ', ' + this.formatDateShort(signed.date),
            description: 'Meal breaks changed',
            oldValue: signed.lunches || '—',
            newValue: current.lunches || '—'
          });
        }
      }

      // Dispute decisions made after signing
      const disputeKeys = new Set([...Object.keys(signedDisputes), ...Object.keys(currentDisputes)]);
      disputeKeys.forEach(key => {
        const before = (signedDisputes[key] || []).slice().sort().join(', ');
        const after = (currentDisputes[key] || []).slice().sort().join(', ');
        if (before === after) return;
        const [date, direction] = key.split('|');
        changes.push({
          type: 'dispute',
          dayLabel: this.formatDayOfWeek(date) + ', ' + this.formatDateShort(date),
          description: `Punch ${direction.toUpperCase()} dispute decided`,
          oldValue: before || '—',
          newValue: after || '—'
        });
      });

      // Check for new punches added after signing
      if (newPunches.length > 0) {
        // Group new punches by date for cleaner display
        const newPunchesByDate = {};
        newPunches.forEach(punch => {
          if (!newPunchesByDate[punch.date]) {
            newPunchesByDate[punch.date] = [];
          }
          newPunchesByDate[punch.date].push(punch);
        });

        for (const [date, punches] of Object.entries(newPunchesByDate)) {
          const punchSummary = punches.map(p => `${p.direction.toUpperCase()}: ${p.actualTime}`).join(', ');
          changes.push({
            type: 'added',
            dayLabel: this.formatDayOfWeek(date) + ', ' + this.formatDateShort(date),
            description: 'New punch records added',
            oldValue: '—',
            newValue: punchSummary
          });
        }
      }

      this.unseenChanges = changes;
    },
    formatDayOfWeek(dateStr) {
      return moment(dateStr, "YYYY-MM-DD").format("ddd");
    },
    formatDateShort(dateStr) {
      return moment(dateStr, "YYYY-MM-DD").format("MMM D");
    },
    confirmDeleteUpload(upload) {
      this.uploadToDelete = upload;
      this.deleteUploadDialog = true;
    },
    async deleteUpload() {
      if (!this.uploadToDelete) return;

      this.deletingUpload = true;
      try {
        // The file lives in the employee app's Firebase project, not this one, so the
        // delete goes through that project's endpoint - it removes the Storage object
        // as well as the Firestore field. Dropping only the field here would leave a
        // publicly readable ID document at a URL nothing references.
        const idToken = await fb.auth.currentUser.getIdToken();

        await axios.post(
          EMPLOYEE_APP_DELETE_DOCUMENT_URL,
          {
            employeeId: this.uploadToDelete.employeeId,
            documentType: this.uploadToDelete.type
          },
          { headers: { Authorization: `Bearer ${idToken}` } }
        );

        const deletedId = this.uploadToDelete.id;
        this.uploads = this.uploads.filter(upload => upload.id !== deletedId);

        this.deleteUploadDialog = false;
        this.uploadToDelete = null;
        this.showSnackbar("Document deleted", "success");
      } catch (error) {
        console.error("Error deleting upload:", error);
        const message = error.response && error.response.data && error.response.data.error;
        this.showSnackbar(message || "Could not delete the document", "error");
      } finally {
        this.deletingUpload = false;
      }
    },
    formatDateTimeFull(value) {
      // The shared formatters drop the year (M/D and ddd, MMM D). Uploads, expiry
      // dates and disputes all outlive a single year, so they get their own format.
      if (!value) return "-";
      let date;
      if (value.toDate) {
        date = value.toDate();
      } else if (value.seconds) {
        date = new Date(value.seconds * 1000);
      } else if (value._seconds) {
        date = new Date(value._seconds * 1000);
      } else {
        date = new Date(value);
      }
      return moment(date).format("MMM D, YYYY h:mm A");
    },
    openEditExpiration(upload) {
      this.uploadToEdit = upload;
      this.editExpirationDate = upload.expirationDate || null;
      // Left unset rather than guessed when nothing has been checked yet, so saving
      // cannot quietly assert a status nobody chose
      this.editExpirationStatus =
        (upload.expirationCheck && upload.expirationCheck.status) || null;
      this.editExpirationDialog = true;
    },
    async saveExpirationEdit() {
      if (!this.uploadToEdit || !this.editExpirationDate) return;

      const upload = this.uploadToEdit;
      const previous = upload.expirationDate || null;
      const dateChanged = previous !== this.editExpirationDate;

      this.savingExpiration = true;
      try {
        // employeeApp lives in this project's Firestore, so this writes directly -
        // unlike deleting a document, which needs the employee app's function to
        // remove the file from its Storage bucket as well.
        //
        // Dotted paths, so datesFound and checkedAt survive: replacing the whole
        // expirationCheck object would throw away what OCR actually read, which is the
        // only evidence of why the row was flagged.
        const updates = {
          [`${upload.type}.expirationDate`]: this.editExpirationDate,
          [`${upload.type}.expirationCheck.status`]: this.editExpirationStatus,
          [`${upload.type}.expirationCheck.overriddenBy`]: this.$store.state.user.uid,
          [`${upload.type}.expirationCheck.overriddenByName`]:
            this.$store.state.user.name || this.$store.state.user.email,
          [`${upload.type}.expirationCheck.overriddenAt`]: fb.firebase.firestore.FieldValue.serverTimestamp()
        };
        if (dateChanged) {
          updates[`${upload.type}.expirationCheck.previousExpirationDate`] = previous;
        }

        await fb.db
          .collection("employeeApp")
          .doc(upload.employeeId)
          .update(updates);

        // Patched in place rather than reloading - loadUploads re-reads the whole
        // employeeApp collection and the employee directory
        upload.expirationDate = this.editExpirationDate;
        upload.expired = moment(this.editExpirationDate, "YYYY-MM-DD").isBefore(
          moment().startOf("day")
        );
        upload.expirationCheck = {
          ...(upload.expirationCheck || {}),
          status: this.editExpirationStatus
        };

        this.editExpirationDialog = false;
        this.uploadToEdit = null;
        this.showSnackbar("Expiration updated", "success");
      } catch (error) {
        console.error("Error updating expiration:", error);
        this.showSnackbar("Could not update the expiration", "error");
      } finally {
        this.savingExpiration = false;
      }
    },
    expirationCheckTooltip(upload) {
      const dates = (upload.expirationCheck && upload.expirationCheck.datesFound) || [];

      if (!dates.length) {
        return "No dates could be read from this image. Worth opening to check.";
      }

      // Says WHY it is flagged, so payroll can judge it here rather than opening the
      // image. Phrased as a prompt, not a verdict: cards carry issue dates and dates
      // of birth too, and OCR misreads digits on a creased card.
      return (
        "This date was not found on the document. Dates read from the image: " +
        dates.map(d => this.formatExpiry(d)).join(", ") + "."
      );
    },
    formatExpiry(dateString) {
      return dateString ? moment(dateString, "YYYY-MM-DD").format("MMM D, YYYY") : "-";
    },
    loadEmployeeDirectory() {
      // Names and badge numbers, keyed by employee id. Cached for the life of the
      // page: most tabs need it and it is a whole-collection read, so doing it per tab
      // reads employees again for the same data.
      //
      // The in-flight request is cached, not just the result. Timesheets, disputes
      // and sick days all ask for it on mount, before any of them has an answer, so
      // caching only the result still read the collection once per caller.
      if (!this.employeeDirectoryRequest) {
        this.employeeDirectoryRequest = fb.db
          .collection("employees")
          .get()
          .then(snapshot => {
            const directory = {};
            snapshot.forEach(doc => {
              const data = doc.data();
              directory[doc.id] = {
                name: `${data.firstName || ""} ${data.lastName || ""}`.trim(),
                // barcodeId is the badge number scanned at registration
                badgeNumber: data.barcodeId || "",
                department: data.department || "",
                terminated: !!data.terminateDate
              };
            });
            this.employeeDirectory = directory;
            return directory;
          })
          .catch(error => {
            // Let the next caller try again rather than inherit the failure
            this.employeeDirectoryRequest = null;
            throw error;
          });
      }
      return this.employeeDirectoryRequest;
    },
    async loadUploads() {
      // There is no uploads collection - each employeeApp document carries at most one
      // sstCard and one idDocument, so the table is those two fields flattened into a
      // row each. A re-upload overwrites, so this is current state, not a history.
      this.loadingUploads = true;
      this.uploadsError = null;
      try {
        const [appSnapshot, employees] = await Promise.all([
          fb.db.collection("employeeApp").get(),
          this.loadEmployeeDirectory()
        ]);

        const today = moment().startOf("day");
        const types = [
          { key: "sstCard", label: "SST Card" },
          { key: "idDocument", label: "ID Document" }
        ];

        const rows = [];
        appSnapshot.forEach(doc => {
          const data = doc.data();
          types.forEach(({ key, label }) => {
            const document = data[key];
            if (!document || !document.url) return;

            rows.push({
              id: `${doc.id}:${key}`,
              employeeId: doc.id,
              // Fall back to the id so a document is never hidden just because the
              // employee record is missing
              employeeName: (employees[doc.id] && employees[doc.id].name) || doc.id,
              badgeNumber: (employees[doc.id] && employees[doc.id].badgeNumber) || "-",
              type: key,
              typeLabel: label,
              uploadedAt: document.uploadedAt && document.uploadedAt.toDate
                ? document.uploadedAt.toDate()
                : null,
              expirationDate: document.expirationDate || null,
              // Written by the employee app after reading the image with Google
              // Vision. Absent on anything uploaded before that existed.
              expirationCheck: document.expirationCheck || null,
              expired: document.expirationDate
                ? moment(document.expirationDate, "YYYY-MM-DD").isBefore(today)
                : false,
              url: document.url
            });
          });
        });

        this.uploads = rows;
        this.uploadsLoaded = true;
      } catch (error) {
        console.error("Error loading uploads:", error);
        this.uploadsError = "Could not load uploads. Check Firestore permissions for employeeApp and employees.";
      } finally {
        this.loadingUploads = false;
      }
    },
    chunkForInQuery(ids) {
      // This app is on Firebase 7.10.0, whose `in` operator accepts a maximum of TEN
      // values - not the 30 that newer SDKs allow. Over the limit the query throws, and
      // both callers only console.error, so the failure is invisible: payroll would see
      // a timesheet with no "changed since signed" warning and assume nothing changed.
      const chunks = [];
      for (let i = 0; i < ids.length; i += 10) {
        chunks.push(ids.slice(i, i + 10));
      }
      return chunks;
    },
    async refreshTimesheetDisputes(timesheet) {
      try {
        // EVERY punch, not just the outs - a dispute can belong to a clock in now
        const punchIds = [];
        timesheet.dailyRecords.forEach(record => {
          (record.punchTimeline || []).forEach(punch => {
            if (punch.id) punchIds.push(punch.id);
          });
        });

        if (punchIds.length === 0) return;

        // Keyed by punch id so a day can collect one dispute from its in punch and
        // another from its out. Both id fields are queried: which one is set is what
        // says what the dispute is about.
        const disputesByPunchId = {};
        const seenDisputeIds = new Set();

        const addDispute = (punchId, entry) => {
          if (!punchId) return;
          if (!disputesByPunchId[punchId]) disputesByPunchId[punchId] = [];
          disputesByPunchId[punchId].push(entry);
        };

        for (const field of ["punchOutId", "punchInId"]) {
          for (const chunk of this.chunkForInQuery(punchIds)) {
            const snapshot = await fb.db
              .collection("employeeAppPunchDisputes")
              .where(field, "in", chunk)
              .get();

            snapshot.forEach(doc => {
              // A record written before disputes became per-punch has both ids, so it
              // comes back from both queries
              if (seenDisputeIds.has(doc.id)) return;
              seenDisputeIds.add(doc.id);

              const dispute = doc.data();
              // Null for a record written before disputes were per-punch: it carries
              // both ids and covers the pair, so labelling it "clock out" would
              // attribute it to a punch it was not specifically about
              const isLegacyPair = !!dispute.punchInId && !!dispute.punchOutId;
              const entry = {
                id: doc.id,
                direction: isLegacyPair ? null : (dispute.punchInId ? "in" : "out"),
                reason: dispute.disputeReason || "",
                status: dispute.disputeStatus || "pending",
                resolutionNotes: dispute.disputeResolutionNotes || null,
                resolvedByName: dispute.disputeResolvedByName || null,
                resolvedAt: dispute.disputeResolvedAt || null
              };
              addDispute(dispute.punchOutId, entry);
              addDispute(dispute.punchInId, entry);
            });
          }
        }

        timesheet.dailyRecords.forEach(record => {
          // No `break` - a day showing only its first dispute would hide the other
          const seen = new Set();
          const found = [];

          (record.punchTimeline || []).forEach(punch => {
            if (!punch.id) return;
            (disputesByPunchId[punch.id] || []).forEach(entry => {
              if (seen.has(entry.id)) return;
              seen.add(entry.id);
              found.push(entry);
            });
          });

          if (found.length) {
            record.disputed = true;
            record.disputes = found;
          }
        });

        // Force reactivity update
        this.selectedTimesheet = { ...timesheet };
      } catch (error) {
        console.error("Error refreshing dispute data:", error);
      }
    },
    async loadOvertimeReasons(timesheet) {
      try {
        // Collect the OUT punches - the reason is only ever recorded on a clock out
        const outPunchIds = [];
        (timesheet.dailyRecords || []).forEach(record => {
          (record.punchTimeline || []).forEach(punch => {
            if (punch.direction === 'out' && punch.id) {
              outPunchIds.push(punch.id);
            }
          });
        });

        if (outPunchIds.length === 0) return;

        // Read the punch records themselves rather than the dailyRecords snapshot the
        // employee signed - the reason was never copied into it, so this is the only
        // way timesheets signed before today show one
        const punchDocs = await Promise.all(
          outPunchIds.slice(0, 40).map(id => fb.db.collection("punchRecords").doc(id).get())
        );

        const reasonMap = {};
        punchDocs.forEach(doc => {
          if (!doc.exists) return;
          const reason = doc.data().lateClockOutReason;
          if (reason) reasonMap[doc.id] = reason;
        });

        if (Object.keys(reasonMap).length === 0) return;

        // A split shift can have more than one late clock out in a day
        (timesheet.dailyRecords || []).forEach(record => {
          const reasons = (record.punchTimeline || [])
            .filter(punch => punch.direction === 'out' && reasonMap[punch.id])
            .map(punch => reasonMap[punch.id]);
          if (reasons.length) {
            record.lateClockOutReason = reasons.join("\n");
          }
        });

        // Force reactivity update
        this.selectedTimesheet = { ...timesheet };
      } catch (error) {
        console.error("Error loading overtime reasons:", error);
      }
    },
    applyTimesheetPatch(timesheetId, patch) {
      // Patch the row in the backing arrays, not just selectedTimesheet.
      // refreshTimesheetDisputes replaces selectedTimesheet with a COPY of the row, so
      // mutating it alone leaves the table showing the old status until a reload.
      Object.assign(this.selectedTimesheet, patch);

      [this.timesheets, this.requestTimesheets].forEach(list => {
        const row = list.find(item => item.id === timesheetId);
        if (row) Object.assign(row, patch);
      });
    },
    async approveTimesheet() {
      if (!this.selectedTimesheet) return;

      this.actionLoading = true;
      try {
        const docRef = fb.db
          .collection("employeeAppTimesheetConfirmations")
          .doc(this.selectedTimesheet.id);

        // Save history entry before making changes
        await fb.db.collection("employeeAppTimesheetHistory").add({
          timesheetId: this.selectedTimesheet.id,
          action: "approved",
          previousStatus: this.selectedTimesheet.status,
          previousSignature: this.selectedTimesheet.signature || null,
          previousReviewNotes: this.selectedTimesheet.reviewNotes || null,
          previousInternalNotes: this.selectedTimesheet.internalNotes || null,
          newReviewNotes: this.reviewNotes || null,
          newInternalNotes: this.internalNotes || null,
          changedBy: this.$store.state.user.uid,
          changedByName: this.$store.state.user.name || this.$store.state.user.email,
          changedAt: fb.firebase.firestore.FieldValue.serverTimestamp()
        });

        // Update the timesheet
        await docRef.update({
          status: "approved",
          reviewNotes: this.reviewNotes || null,
          internalNotes: this.internalNotes || null,
          reviewedBy: this.$store.state.user.uid,
          reviewedByName: this.$store.state.user.name || this.$store.state.user.email,
          reviewedAt: fb.firebase.firestore.FieldValue.serverTimestamp()
        });

        this.applyTimesheetPatch(this.selectedTimesheet.id, {
          status: "approved",
          reviewNotes: this.reviewNotes,
          internalNotes: this.internalNotes,
          reviewedByName: this.$store.state.user.name || this.$store.state.user.email,
          reviewedAt: new Date()
        });

        this.showSnackbar("Timesheet approved", "success");
        this.reviewNotes = "";
        this.internalNotes = "";
        this.dialogOpen = false;
      } catch (error) {
        console.error("Error approving timesheet:", error);
        this.showSnackbar("Error approving timesheet", "error");
      } finally {
        this.actionLoading = false;
      }
    },
    async requestReconfirmation() {
      if (!this.selectedTimesheet || !this.reviewNotes.trim()) return;

      this.actionLoading = true;
      try {
        const docRef = fb.db
          .collection("employeeAppTimesheetConfirmations")
          .doc(this.selectedTimesheet.id);

        // Save history entry before making changes (include daily records for "What Changed" comparison)
        await fb.db.collection("employeeAppTimesheetHistory").add({
          timesheetId: this.selectedTimesheet.id,
          action: "requested_reconfirmation",
          previousStatus: this.selectedTimesheet.status,
          previousSignature: this.selectedTimesheet.signature || null,
          previousReviewNotes: this.selectedTimesheet.reviewNotes || null,
          previousInternalNotes: this.selectedTimesheet.internalNotes || null,
          previousDailyRecords: this.selectedTimesheet.dailyRecords || null,
          previousWeeklyTotal: this.selectedTimesheet.weeklyTotal || null,
          newReviewNotes: this.reviewNotes,
          newInternalNotes: this.internalNotes || null,
          changedBy: this.$store.state.user.uid,
          changedByName: this.$store.state.user.name || this.$store.state.user.email,
          changedAt: fb.firebase.firestore.FieldValue.serverTimestamp()
        });

        // Update the timesheet
        await docRef.update({
          status: "needs_reconfirmation",
          reviewNotes: this.reviewNotes,
          internalNotes: this.internalNotes || null,
          reviewedBy: this.$store.state.user.uid,
          reviewedByName: this.$store.state.user.name || this.$store.state.user.email,
          reviewedAt: fb.firebase.firestore.FieldValue.serverTimestamp()
        });

        this.applyTimesheetPatch(this.selectedTimesheet.id, {
          status: "needs_reconfirmation",
          reviewNotes: this.reviewNotes,
          internalNotes: this.internalNotes,
          reviewedByName: this.$store.state.user.name || this.$store.state.user.email,
          reviewedAt: new Date()
        });

        this.showSnackbar("Re-confirmation requested from employee", "success");
        this.reviewNotes = "";
        this.internalNotes = "";
        this.dialogOpen = false;
      } catch (error) {
        console.error("Error requesting re-confirmation:", error);
        this.showSnackbar("Error requesting re-confirmation", "error");
      } finally {
        this.actionLoading = false;
      }
    },
    openPdf(timesheet) {
      if (timesheet.pdfUrl) {
        window.open(timesheet.pdfUrl, "_blank");
      }
    },
    // Dispute methods
    openDisputeDialog(dispute) {
      this.selectedDispute = dispute;
      this.disputeResolutionNotes = "";
      this.disputeDialogOpen = true;
    },
    getDisputedDays(timesheet) {
      if (!timesheet || !timesheet.dailyRecords) return [];
      return timesheet.dailyRecords.filter(day => day.disputed);
    },
    getDisputeStatusColor(status) {
      switch (status) {
        case "approved": return "success";
        case "closed": return "orange";
        case "resolved": return "orange"; // Legacy support
        case "denied": return "orange"; // Legacy support
        default: return "warning"; // Pending
      }
    },
    getDisputeStatusLabel(status) {
      // Used where the column IS the status, so pending reads as "Pending".
      // getDisputeStatusText below is for the timesheet modal, where the chip is a
      // marker on a day and "Disputed" is the more useful word.
      switch (status) {
        case "approved": return "Approved";
        case "closed": return "Closed";
        case "resolved": return "Closed"; // Legacy support
        case "denied": return "Denied"; // Legacy support
        default: return "Pending";
      }
    },
    getDisputeStatusText(status) {
      switch (status) {
        case "approved": return "Approved";
        case "closed": return "Closed";
        case "resolved": return "Closed"; // Legacy support
        case "denied": return "Denied"; // Legacy support
        default: return "Disputed";
      }
    },
    dayDisputes(record) {
      // A day can hold a dispute on its clock in and another on its clock out.
      // Timesheets signed before that was true have only the flat fields, so those
      // are folded into the same shape rather than branched on everywhere.
      if (!record) return [];
      if (record.disputes && record.disputes.length) return record.disputes;
      if (!record.disputed) return [];
      return [
        {
          direction: null,
          reason: record.disputeReason || "",
          status: record.disputeStatus || "pending",
          resolutionNotes: record.disputeResolutionNotes || null,
          resolvedByName: record.disputeResolvedByName || null,
          resolvedAt: record.disputeResolvedAt || null
        }
      ];
    },
    getTimesheetDisputes(timesheet) {
      // Find all disputes for this timesheet by matching employee and date range
      if (!timesheet || !this.punchDisputes) return [];
      return this.punchDisputes.filter(d => {
        if (d.employee !== timesheet.employee) return false;
        // Check if dispute date falls within timesheet week
        const disputeDate = d.date;
        return disputeDate >= timesheet.weekStart && disputeDate <= timesheet.weekEnd;
      });
    },
    getTimesheetDisputeStatus(timesheet) {
      const disputes = this.getTimesheetDisputes(timesheet);
      if (disputes.length === 0) return null;

      // Check if any disputes are pending
      const hasPending = disputes.some(d => !d.disputeStatus || d.disputeStatus === 'pending');
      if (hasPending) return 'pending';

      // All resolved - check if all approved or mixed
      const allApproved = disputes.every(d => d.disputeStatus === 'approved');
      return allApproved ? 'approved' : 'closed';
    },
    getTimesheetDisputeChipColor(timesheet) {
      const status = this.getTimesheetDisputeStatus(timesheet);
      switch (status) {
        case 'approved': return 'success';
        case 'closed': return 'orange';
        default: return 'warning'; // pending
      }
    },
    getTimesheetDisputeChipText(timesheet) {
      const status = this.getTimesheetDisputeStatus(timesheet);
      switch (status) {
        case 'approved': return 'Dispute Approved';
        case 'closed': return 'Dispute Closed';
        default: return 'Disputed';
      }
    },
    async resolveDispute(resolution) {
      if (!this.selectedDispute || !this.disputeResolutionNotes.trim()) return;
      // Re-checked here, not just on the buttons: the dialog may have been open since
      // before someone else claimed it, and the disabled state was decided then
      if (!this.canActionDispute(this.selectedDispute)) {
        this.showSnackbar(
          this.actionBlockedReason(this.selectedDispute) || "This dispute cannot be actioned",
          "warning"
        );
        return;
      }

      this.actionLoading = true;
      try {
        const updateData = {
          disputeStatus: resolution,
          disputeResolutionNotes: this.disputeResolutionNotes,
          disputeResolvedBy: this.$store.state.user.uid,
          disputeResolvedByName: this.$store.state.user.name || this.$store.state.user.email,
          disputeResolvedAt: fb.firebase.firestore.FieldValue.serverTimestamp(),
          // The claim exists to stop two people working the same open dispute. Once
          // it is resolved there is nothing to collide over, and a claim left behind
          // would make a finished dispute look like someone still owed work on it.
          // foremanReview is deliberately kept: what the foreman said is part of the
          // record of how this was decided.
          claimedBy: fb.firebase.firestore.FieldValue.delete()
        };

        // Update the punch dispute record (disputes are stored separately, not on punch records)
        await fb.db
          .collection("employeeAppPunchDisputes")
          .doc(this.selectedDispute.id)
          .update(updateData);

        // Update local state
        this.selectedDispute.disputeStatus = resolution;
        this.selectedDispute.disputeResolutionNotes = this.disputeResolutionNotes;
        this.selectedDispute.disputeResolvedByName = this.$store.state.user.name || this.$store.state.user.email;
        this.selectedDispute.disputeResolvedAt = new Date();

        this.showSnackbar(`Dispute ${resolution}`, resolution === 'approved' ? 'success' : 'warning');
        this.disputeResolutionNotes = "";
        this.disputeDialogOpen = false;
      } catch (error) {
        console.error("Error resolving dispute:", error);
        this.showSnackbar("Error resolving dispute", "error");
      } finally {
        this.actionLoading = false;
      }
    },
    async loadSickDayRequests() {
      this.loadingSickDays = true;
      this.sickDaysError = null;
      try {
        const [snapshot, directory] = await Promise.all([
          fb.db
            .collection("employeeAppSickDayRequests")
            .orderBy("requestedAt", "desc")
            .limit(300)
            .get(),
          this.loadEmployeeDirectory()
        ]);

        this.sickDayRequests = snapshot.docs.map(doc => {
          const data = doc.data();
          const employee = directory[data.employee];
          return {
            id: doc.id,
            ...data,
            employeeName: data.employeeName || (employee && employee.name) || "Unknown",
            // Requests carry the employee id and name but not the badge or department
            badgeNumber: (employee && employee.badgeNumber) || "-",
            department: (employee && employee.department) || "",
            // Every request is written as "pending", but default it anyway so one
            // without a status cannot drop out of both the Pending and Resolved lists
            status: data.status || "pending",
            // Dates rather than Timestamps, so the table sorts them
            requestedAt: data.requestedAt && data.requestedAt.toDate ? data.requestedAt.toDate() : null,
            // Declared up front so Vue 2 tracks them - a pending request has none of
            // these keys, and resolving one adds them
            resolutionNotes: data.resolutionNotes || null,
            resolvedByName: data.resolvedByName || null,
            resolvedAt: data.resolvedAt && data.resolvedAt.toDate ? data.resolvedAt.toDate() : null
          };
        });

        // The pending chip widens the tab after the slider was positioned
        this.$nextTick(() => {
          if (this.$refs.tabs) this.$refs.tabs.callSlider();
        });
      } catch (error) {
        console.error("Error loading sick day requests:", error);
        this.sickDaysError =
          "Could not load sick day requests. Check Firestore permissions for employeeAppSickDayRequests.";
      } finally {
        this.loadingSickDays = false;
      }
    },
    openSickDayDialog(request) {
      this.selectedSickDay = request;
      this.sickDayNotes = "";
      this.sickDayDialogOpen = true;
    },
    async resolveSickDay(status) {
      const request = this.selectedSickDay;
      const notes = this.sickDayNotes.trim();
      if (!request || (status === "denied" && !notes)) return;

      this.resolvingSickDay = status;
      try {
        const me = this.$store.state.user;
        const resolvedByName = me.name || me.email;
        const ref = fb.db.collection("employeeAppSickDayRequests").doc(request.id);

        // A transaction, so two reviewers deciding the same request at once cannot
        // have the second silently overwrite the first. Returns the request as it
        // stands if it was no longer pending.
        const alreadyDecided = await fb.db.runTransaction(async tx => {
          const doc = await tx.get(ref);
          if (!doc.exists) return { missing: true };
          const current = doc.data();
          if (current.status && current.status !== "pending") return current;
          tx.update(ref, {
            status,
            // Null rather than "" when approved without one, so it cannot read as a
            // note that was left blank
            resolutionNotes: notes || null,
            resolvedBy: me.uid,
            resolvedByName,
            resolvedAt: fb.firebase.firestore.FieldValue.serverTimestamp()
          });
          return null;
        });

        if (alreadyDecided) {
          this.sickDayDialogOpen = false;
          await this.loadSickDayRequests();
          this.showSnackbar(
            alreadyDecided.missing
              ? "This request no longer exists"
              : `${alreadyDecided.resolvedByName || "Someone else"} already ${alreadyDecided.status} this request`,
            "warning"
          );
          return;
        }

        Object.assign(request, {
          status,
          resolutionNotes: notes || null,
          resolvedByName,
          resolvedAt: new Date()
        });
        this.sickDayDialogOpen = false;
        this.showSnackbar(
          status === "approved" ? "Sick day approved" : "Sick day denied",
          status === "approved" ? "success" : "warning"
        );
      } catch (error) {
        console.error("Error resolving sick day request:", error);
        this.showSnackbar("Could not save this decision", "error");
      } finally {
        this.resolvingSickDay = null;
      }
    },
    async loadJSZip() {
      if (window.JSZip) return window.JSZip;

      return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = "https://cdn.jsdelivr.net/npm/jszip@3.10.1/dist/jszip.min.js";
        script.onload = () => resolve(window.JSZip);
        script.onerror = () => reject(new Error("Failed to load JSZip"));
        document.head.appendChild(script);
      });
    },
    async downloadAllPdfs() {
      if (!this.selectedRequest) return;

      const timesheetsWithPdfs = this.requestTimesheets.filter(ts => ts.pdfUrl);
      if (timesheetsWithPdfs.length === 0) {
        this.showSnackbar("No PDFs available for this request", "warning");
        return;
      }

      this.downloadingPdfs = true;
      this.showSnackbar(`Downloading ${timesheetsWithPdfs.length} PDFs...`, "info");

      try {
        const JSZip = await this.loadJSZip();
        const zip = new JSZip();

        const weekLabel = this.formatWeekRange(this.selectedRequest.weekStart, this.selectedRequest.weekEnd).replace(/[^a-zA-Z0-9-]/g, "_");

        const fetchPromises = timesheetsWithPdfs.map(async (ts, index) => {
          try {
            const response = await fetch(ts.pdfUrl);
            if (!response.ok) throw new Error(`Failed to fetch PDF`);
            const blob = await response.blob();
            const fileName = `${ts.employeeName || 'Employee'}_${ts.weekStart || index}.pdf`.replace(/[^a-zA-Z0-9_.-]/g, "_");
            zip.file(fileName, blob);
            return { success: true, name: ts.employeeName };
          } catch (error) {
            console.error(`Error fetching PDF for ${ts.employeeName}:`, error);
            return { success: false, name: ts.employeeName };
          }
        });

        const results = await Promise.all(fetchPromises);
        const successCount = results.filter(r => r.success).length;

        if (successCount === 0) {
          this.showSnackbar("Failed to download any PDFs", "error");
          return;
        }

        const zipBlob = await zip.generateAsync({ type: "blob" });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(zipBlob);
        link.download = `Timesheets_${weekLabel}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);

        this.showSnackbar(`Downloaded ${successCount} of ${timesheetsWithPdfs.length} PDFs`, "success");
      } catch (error) {
        console.error("Error creating zip file:", error);
        this.showSnackbar("Error creating zip file", "error");
      } finally {
        this.downloadingPdfs = false;
      }
    },
    formatWeekRange(start, end) {
      const s = moment(start);
      const e = moment(end);
      if (s.month() === e.month()) {
        return `${s.format("MMM D")} - ${e.format("D, YYYY")}`;
      }
      return `${s.format("MMM D")} - ${e.format("MMM D, YYYY")}`;
    },
    formatDate(date) {
      return moment(date).format("ddd, MMM D");
    },
    formatDateTime(date) {
      if (!date) return "-";
      let d;
      if (date.toDate) {
        d = date.toDate();
      } else if (date.seconds) {
        d = new Date(date.seconds * 1000);
      } else if (date._seconds) {
        d = new Date(date._seconds * 1000);
      } else if (typeof date === 'string' && date.includes('seconds=')) {
        // Parse string format: "Timestamp(seconds=1783901701, nanoseconds=963000000)"
        const match = date.match(/seconds=(\d+)/);
        if (match) {
          d = new Date(parseInt(match[1]) * 1000);
        } else {
          return "-";
        }
      } else {
        d = new Date(date);
      }
      return moment(d).format("MMM D, YYYY h:mm A");
    },
    formatShortDateTime(date) {
      if (!date) return "";
      let d;
      if (date.toDate) {
        d = date.toDate();
      } else if (date.seconds) {
        d = new Date(date.seconds * 1000);
      } else if (date._seconds) {
        d = new Date(date._seconds * 1000);
      } else if (typeof date === 'string' && date.includes('seconds=')) {
        // Parse string format: "Timestamp(seconds=1783901701, nanoseconds=963000000)"
        const match = date.match(/seconds=(\d+)/);
        if (match) {
          d = new Date(parseInt(match[1]) * 1000);
        } else {
          return "";
        }
      } else {
        d = new Date(date);
      }
      return moment(d).format("M/D h:mm A");
    },
    formatHours(hours) {
      if (typeof hours === 'number') {
        return hours.toFixed(2);
      }
      return hours || "0.00";
    },
    getStatusChipColor(status) {
      switch (status) {
        case "approved": return "success";
        case "denied": return "error";
        case "needs_reconfirmation": return "orange";
        default: return "warning";
      }
    },
    getStatusText(status) {
      switch (status) {
        case "approved": return "Approved";
        case "denied": return "Denied";
        case "needs_reconfirmation": return "Needs Re-sign";
        case "pending": return "Pending";
        default: return "Pending";
      }
    },
    showSnackbar(text, color) {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
    getSignatureUrl(signature) {
      if (!signature) return "";
      if (typeof signature === "object") {
        if (signature.signatureData) return signature.signatureData;
        if (signature.url) return signature.url;
        if (signature.data) return this.getSignatureUrl(signature.data);
        return "";
      }
      const sigStr = String(signature);
      if (sigStr.startsWith("data:") || sigStr.startsWith("http")) {
        return sigStr;
      }
      return `data:image/png;base64,${sigStr}`;
    },
    // Timesheet Request methods
    allowedStartDates(date) {
      // Allow any date - payroll can choose any date range
      return true;
    },
    async loadTimesheetRequests() {
      this.loadingRequests = true;
      try {
        const snapshot = await fb.db
          .collection("employeeAppTimesheetRequests")
          .orderBy("createdAt", "desc")
          .limit(100)
          .get();

        const requests = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // For each request, count how many timesheets have been submitted
        for (const request of requests) {
          const confirmationsSnapshot = await fb.db
            .collection("employeeAppTimesheetConfirmations")
            .where("timesheetRequestId", "==", request.id)
            .get();
          request.submittedCount = confirmationsSnapshot.size;
        }

        this.timesheetRequests = requests;
      } catch (error) {
        console.error("Error loading timesheet requests:", error);
        this.showSnackbar("Error loading timesheet requests", "error");
      } finally {
        this.loadingRequests = false;
      }
    },
    async createTimesheetRequest() {
      if (!this.canCreateRequest) return;

      this.creatingRequest = true;
      try {
        // Snapshot how many employees this request applies to. Taken once here
        // rather than on every page load - a week of punches is thousands of docs.
        const expectedCount = await this.countEmployeesWithPunches(
          this.newRequestStartDate,
          this.newRequestEndDate
        );

        const requestData = {
          weekStart: this.newRequestStartDate,
          weekEnd: this.newRequestEndDate,
          status: "active",
          createdAt: fb.firebase.firestore.FieldValue.serverTimestamp(),
          createdBy: this.$store.state.user.uid,
          createdByName: this.$store.state.user.name || this.$store.state.user.email,
          expectedCount: expectedCount
        };

        await fb.db.collection("employeeAppTimesheetRequests").add(requestData);

        this.showSnackbar("Timesheet request created", "success");
        this.newRequestStartDate = "";
        this.newRequestEndDate = "";
        await this.loadTimesheetRequests();
      } catch (error) {
        console.error("Error creating timesheet request:", error);
        this.showSnackbar("Error creating timesheet request", "error");
      } finally {
        this.creatingRequest = false;
      }
    },
    confirmCloseRequest(request) {
      this.closeConfirmRequest = request;
      this.closeConfirmDialog = true;
    },
    async executeCloseRequest() {
      if (!this.closeConfirmRequest) return;

      const request = this.closeConfirmRequest;
      this.closingRequestId = request.id;
      try {
        await fb.db
          .collection("employeeAppTimesheetRequests")
          .doc(request.id)
          .update({
            status: "closed",
            closedAt: fb.firebase.firestore.FieldValue.serverTimestamp(),
            closedBy: this.$store.state.user.uid,
            closedByName: this.$store.state.user.name || this.$store.state.user.email
          });

        request.status = "closed";
        this.showSnackbar("Timesheet request closed", "success");
        this.closeConfirmDialog = false;
        this.closeConfirmRequest = null;
      } catch (error) {
        console.error("Error closing timesheet request:", error);
        this.showSnackbar("Error closing timesheet request", "error");
      } finally {
        this.closingRequestId = null;
      }
    },
    async reopenTimesheetRequest(request) {
      this.reopeningRequestId = request.id;
      try {
        await fb.db
          .collection("employeeAppTimesheetRequests")
          .doc(request.id)
          .update({
            status: "active",
            reopenedAt: fb.firebase.firestore.FieldValue.serverTimestamp(),
            reopenedBy: this.$store.state.user.uid,
            reopenedByName: this.$store.state.user.name || this.$store.state.user.email
          });

        request.status = "active";
        this.showSnackbar("Timesheet request reopened", "success");
      } catch (error) {
        console.error("Error reopening timesheet request:", error);
        this.showSnackbar("Error reopening timesheet request", "error");
      } finally {
        this.reopeningRequestId = null;
      }
    },
    messageSeenColor(message) {
      if (!message.audienceCount) return "grey";
      if (message.seenCount >= message.audienceCount) return "success";
      return message.seenCount > 0 ? "warning" : "grey";
    },
    messageAudienceLabel(message) {
      return message.departments && message.departments.length
        ? message.departments.join(", ")
        : "all employees";
    },
    resetMessageForm() {
      this.newMessageTitle = "";
      this.newMessageBody = "";
      this.newMessageAudience = "all";
      this.newMessageDepartments = [];
    },
    openMessageForm() {
      this.resetMessageForm();
      this.messageFormDialog = true;
    },
    closeMessageForm() {
      this.messageFormDialog = false;
      this.resetMessageForm();
    },
    async sendPayrollMessage() {
      if (!this.messageIsValid) return;

      this.sendingMessage = true;
      try {
        const message = {
          title: this.newMessageTitle.trim(),
          message: this.newMessageBody.trim(),
          // Null for everyone. The employee app also treats a missing field as
          // everyone, which is every message sent before this existed. Matched against
          // the employee's department when they load notifications, not when it is
          // sent, so someone who changes department gains or loses it.
          departments:
            this.newMessageAudience === "departments" ? [...this.newMessageDepartments] : null,
          createdAt: fb.firebase.firestore.FieldValue.serverTimestamp(),
          // Recorded for our own reference - employees only ever see "from Payroll"
          createdBy: this.$store.state.user.uid,
          createdByName:
            this.$store.state.user.name || this.$store.state.user.email
        };
        await fb.db.collection("employeeAppPayrollMessages").add(message);

        this.messageFormDialog = false;
        this.resetMessageForm();
        this.showSnackbar(`Message sent to ${this.messageAudienceLabel(message)}`, "success");
        await this.loadPayrollMessages();
      } catch (error) {
        console.error("Error sending payroll message:", error);
        this.showSnackbar("Error sending message", "error");
      } finally {
        this.sendingMessage = false;
      }
    },
    async loadPayrollMessages() {
      this.loadingMessages = true;
      try {
        // Every message, not just the last 30 days - employees stop seeing them after
        // 30 days, but payroll wants the full history of what was sent
        const snapshot = await fb.db
          .collection("employeeAppPayrollMessages")
          .orderBy("createdAt", "desc")
          .limit(200)
          .get();

        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        // Loaded once and reused for every message's seen count, rather than per message.
        // Kept on the component too, for the compose dialog's departments and counts.
        const audience = await this.loadMessageAudience();
        this.messageAudience = audience;

        this.payrollMessages = snapshot.docs.map(doc => {
          const data = doc.data();
          const createdAt = data.createdAt && data.createdAt.toDate
            ? data.createdAt.toDate()
            : null;

          return {
            id: doc.id,
            ...data,
            // Flag the ones employees can no longer see, so a message still listed here
            // isn't mistaken for one that's still reaching people
            expired: !!createdAt && createdAt < thirtyDaysAgo,
            ...this.countMessageSeen(doc.id, data.departments, audience)
          };
        });
      } catch (error) {
        console.error("Error loading payroll messages:", error);
        this.showSnackbar("Error loading messages", "error");
      } finally {
        this.loadingMessages = false;
      }
    },
    async loadMessageAudience() {
      // Everyone registered for the app, with when they registered and when they last
      // opened their notifications. Three collection reads, done once per tab load and
      // reused across every message rather than queried per message.
      //
      // Read individually so a Firestore rule blocking one of them names that collection
      // in the console instead of failing the whole tab with a generic permissions error.
      const readCollection = async (name) => {
        try {
          return await fb.db.collection(name).get();
        } catch (error) {
          console.error(`Seen counts unavailable - cannot read "${name}":`, error.message);
          return null;
        }
      };

      const [appSnapshot, employeesSnapshot, seenSnapshot] = await Promise.all([
        readCollection("employeeApp"),
        readCollection("employees"),
        readCollection("employeeAppNotifications")
      ]);

      // Without all three the count would be wrong rather than merely missing, so drop it
      if (!appSnapshot || !employeesSnapshot || !seenSnapshot) return null;

      const terminated = new Set(
        employeesSnapshot.docs.filter(doc => doc.data().terminateDate).map(doc => doc.id)
      );
      const departmentById = {};
      employeesSnapshot.forEach(doc => {
        departmentById[doc.id] = normalizeDepartment(doc.data().department);
      });

      const seenPayrollMessages = {};
      seenSnapshot.forEach(doc => {
        seenPayrollMessages[doc.id] = doc.data().seenPayrollMessageIds || [];
      });

      const members = [];
      appSnapshot.forEach(doc => {
        const data = doc.data();

        // Never registered for the app, or can't sign in any more - they were never
        // going to see it, so counting them would permanently depress every figure
        if (!data.firebaseAuthUid || terminated.has(doc.id)) return;

        members.push({
          department: departmentById[doc.id] || "",
          seenPayrollMessageIds: seenPayrollMessages[doc.id] || []
        });
      });

      return members;
    },
    countMessageSeen(messageId, departments, audience) {
      // No audience means a collection couldn't be read - show the message without a count
      if (!audience) return { seenCount: null, audienceCount: null };

      // A message sent to departments counts only their members. Matched on current
      // department, as the employee app does, so the two agree on who it reaches.
      if (departments && departments.length) {
        const wanted = departments.map(normalizeDepartment);
        audience = audience.filter(member => wanted.includes(member.department));
      }

      // Everyone with app access who isn't terminated. Not filtered by when they
      // registered: someone who registers later still sees messages from the last 30
      // days, so they belong in the denominator.
      //
      // The employee app records the message IDs it actually put on screen when the
      // employee closed the dialog - so this is a record of being shown the message,
      // not an inference from when they last opened their notifications.
      const seen = audience.filter(member =>
        member.seenPayrollMessageIds.includes(messageId)
      );

      return { seenCount: seen.length, audienceCount: audience.length };
    },
    confirmDeleteMessage(message) {
      this.messageToDelete = message;
      this.deleteMessageConfirmDialog = true;
    },
    async deletePayrollMessage() {
      if (!this.messageToDelete) return;

      this.deletingMessageId = this.messageToDelete.id;
      try {
        await fb.db
          .collection("employeeAppPayrollMessages")
          .doc(this.messageToDelete.id)
          .delete();

        this.deleteMessageConfirmDialog = false;
        this.messageToDelete = null;
        this.showSnackbar("Message deleted", "success");
        await this.loadPayrollMessages();
      } catch (error) {
        console.error("Error deleting payroll message:", error);
        this.showSnackbar("Error deleting message", "error");
      } finally {
        this.deletingMessageId = null;
      }
    },
    async loadUnsignedEmployees() {
      // Everyone the request applies to, less everyone with a signed timesheet for the
      // week. Matched on the week as well as the request: a confirmation is one per
      // employee per week, so one signed under another request for the same week counts.
      if (!this.selectedRequest) return;
      this.loadingUnsigned = true;
      try {
        const { weekStart, weekEnd } = this.selectedRequest;
        const [expected, weekSnapshot, directory] = await Promise.all([
          this.employeesWithPunches(weekStart, weekEnd),
          fb.db.collection("employeeAppTimesheetConfirmations").where("weekStart", "==", weekStart).get(),
          this.loadEmployeeDirectory()
        ]);

        const confirmations = [
          ...this.requestTimesheets,
          ...weekSnapshot.docs.map(doc => doc.data()).filter(data => data.weekEnd === weekEnd)
        ];
        const signed = new Set(confirmations.map(ts => ts.employee));
        const toRow = id => ({
          id,
          name: (directory[id] && directory[id].name) || "Unknown employee",
          badgeNumber: (directory[id] && directory[id].badgeNumber) || "-"
        });
        const byName = (a, b) => a.name.localeCompare(b.name);

        this.unsignedEmployees = expected.filter(id => !signed.has(id)).map(toRow).sort(byName);
        this.resignEmployees = [
          ...new Set(confirmations.filter(ts => ts.status === "needs_reconfirmation").map(ts => ts.employee))
        ].map(toRow).sort(byName);
      } catch (error) {
        console.error("Error loading unsigned employees:", error);
        this.showSnackbar("Error loading who hasn't signed", "error");
      } finally {
        this.loadingUnsigned = false;
      }
    },
    async countEmployeesWithPunches(weekStart, weekEnd) {
      // How many employees this timesheet request applies to - the denominator for
      // "X of Y submitted".
      return (await this.employeesWithPunches(weekStart, weekEnd)).length;
    },
    async employeesWithPunches(weekStart, weekEnd) {
      // The employees this timesheet request applies to, by id
      const start = new Date(weekStart + "T00:00:00");
      const end = new Date(weekEnd + "T23:59:59");

      const snapshot = await fb.db
        .collection("punchRecords")
        .where("time", ">=", start)
        .where("time", "<=", end)
        .get();

      // Count only employees who STARTED a shift in the week. A shift belongs to the
      // week containing its punch IN, so someone whose only punch here is the OUT of a
      // shift that started the previous week is not shown this request by the employee
      // app - counting them would make the denominator unreachable.
      const employeeIds = new Set();
      snapshot.forEach(doc => {
        const data = doc.data();
        if (data.direction === "in" && data.employee) {
          employeeIds.add(data.employee);
        }
      });

      if (employeeIds.size === 0) return [];

      // Terminated employees can't sign into the employee app at all, so they would
      // inflate the count forever. Filtered in code because this SDK has no "!=" and
      // terminateDate's type isn't guaranteed - truthiness is what the employee app's
      // auth middleware checks too (the directory's terminated flag is the same test).
      const directory = await this.loadEmployeeDirectory();
      return [...employeeIds].filter(id => !(directory[id] && directory[id].terminated));
    },
    async refreshExpectedCount(request) {
      // Re-take the snapshot for one request - punches may have been added or
      // corrected since it was created
      this.refreshingCountId = request.id;
      try {
        const expectedCount = await this.countEmployeesWithPunches(
          request.weekStart,
          request.weekEnd
        );

        await fb.db
          .collection("employeeAppTimesheetRequests")
          .doc(request.id)
          .update({ expectedCount: expectedCount });

        request.expectedCount = expectedCount;
        this.showSnackbar(`Updated - ${expectedCount} employees worked that week`, "success");
      } catch (error) {
        console.error("Error refreshing expected count:", error);
        this.showSnackbar("Error refreshing count", "error");
      } finally {
        this.refreshingCountId = null;
      }
    },
    getRequestStatusColor(request) {
      const submitted = request.submittedCount || 0;

      if (typeof request.expectedCount === "number" && request.expectedCount > 0) {
        if (submitted >= request.expectedCount) return "success";
        return submitted > 0 ? "warning" : "grey";
      }

      // No snapshot (requests created before expectedCount existed)
      return submitted > 0 ? "success" : "grey";
    }
  },
  mounted() {
    this.loadTimesheets();
    this.loadPunchDisputes();
    this.loadTimesheetRequests();
    this.loadPayrollMessages();
    this.loadSickDayRequests();
  }
};
</script>

<style scoped>
.v-data-table >>> tbody tr:hover {
  background-color: rgba(25, 118, 210, 0.08) !important;
}
</style>
